/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, {
  useState, useRef, useContext, useEffect, useCallback, useMemo,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import AXIOS from 'axios';
import { AppContext } from 'Context';
import {
  getContributionLinkInfo, submitContribution, createUploadSSESessionPublic, subscribeToUploadStatus,
  listMyContributions, deleteMyContribution, getContributionSubscription,
} from 'Api/apiFunctions';
import {
  createGiftUploadSession,
  completeGiftUploadSession,
  uploadPartWithPresignedUrl,
} from 'Api/uploaderApiFunctions';
import uploadManagerSecure from 'Utils/uploadManagerSecure';
import { generateS3Url } from 'Api/awsConfig';
import { uploadLargeFile } from 'Utils/uploadWrapper';
import { resolveContributionTitle } from 'Utils/contributionTitle';
import CloudUploadModal from 'Components/UploadDropdown/CloudUploadModal';
import QRCodeComponent from 'Utils/QRCodeComponent';
import LightRays from 'Components/reactbits/LightRays';
import styles from './index.module.css';

// ── Constants ────────────────────────────────────────────────────────────────────
const UPLOAD_STATUS = {
  PENDING: 'pending',
  UPLOADING: 'uploading',
  PAUSED: 'paused',
  NETWORK_ERROR: 'network_error',
  COMPLETED: 'completed',
  FAILED: 'failed',
  CANCELLING: 'cancelling',
  CANCELLED: 'cancelled',
};

const VIDEO_EXTENSIONS = ['.mp4', '.mov', '.avi', '.mkv', '.webm', '.flv', '.wmv', '.m4v', '.mpeg', '.mpg'];
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.gif'];
const MAX_TOTAL_SIZE_BYTES = 10 * 1024 * 1024 * 1024; // 10GB
// Free plan (subscription null or id === '6') caps the owner's account at 15
// total items — each video and each individual photo counts as one; an album
// is not an item, its photos are. Every other plan is unlimited.
const FREE_PLAN_ITEM_LIMIT = 15;
const FREE_PLAN_ID = '6';
// Count how many account items a set of selected files represents: a video is
// one item, an album contributes its photo count (each photo is one item).
const countSelectedItems = (files) => files.reduce((sum, f) => {
  if (f.type === 'album') return sum + (f.photoCount || (f.photos || []).length || 0);
  return sum + 1;
}, 0);
const ALL_PHOTOS_ALBUM_NAME = 'All photos';
const FILE_ACCEPT = 'video/*,image/*,.mp4,.mov,.avi,.mkv,.webm,.flv,.wmv,.m4v,.mpeg,.mpg,.jpg,.jpeg,.png,.webp,.heic,.heif,.gif';

// Folder display name from a contributor email (mobile parity): the text before
// "@", first letter capitalized — e.g. "edwin@x.com" → "Edwin". Falls back to
// the raw value (for phone-number contacts) or "Anonymous" when empty.
const folderNameFromEmail = (email) => {
  const raw = (email == null ? '' : String(email)).trim();
  if (!raw) return 'Anonymous';
  const local = raw.includes('@') ? raw.split('@')[0] : raw;
  if (!local) return 'Anonymous';
  return local.charAt(0).toUpperCase() + local.slice(1);
};

const isAlbumCard = (c) => c.type === 'album' || c.isPhotoAlbum || c.fileName === ALL_PHOTOS_ALBUM_NAME;

// Effective photo count for one album card (mobile parity, _MergedContribAlbumCard):
// explicit photoCount → else the photos array length → else 1 when the album
// only carries a cover thumbnail (a photo-less backend album still counts as
// one tile) → else 0.
const effectivePhotoCount = (a) => a.photoCount
  || (a.photos ? a.photos.length : 0)
  || (a.thumbnail || a.posterUrl ? 1 : 0);

// One collage tile per album that has no individual photos but does have a
// cover thumbnail — mobile falls the photo-less album back to its
// thumbnail_signed as a single tile so the merged collage still shows it.
const thumbnailTileFor = (a) => {
  const thumb = a.thumbnail || a.posterUrl || null;
  if (!thumb) return null;
  return {
    id: `${a.id || a.contributionId || 'album'}-cover`,
    name: a.fileName || '',
    previewUrl: thumb,
    thumbnailUrl: thumb,
  };
};

// Collapse every photo-album in a card list into ONE merged album card (same
// idea as the mobile-upload page: one album per contributor instead of a new
// album per upload). Videos are left untouched. Returns [...videos, mergedAlbum]
// or the original list when there's at most one album.
const mergeAlbumCards = (cards) => {
  const albums = cards.filter(isAlbumCard);
  const nonAlbums = cards.filter((c) => !isAlbumCard(c));
  if (albums.length <= 1) return cards;

  // Backend photos first (in list order), photo-less albums contribute their
  // cover thumbnail as one tile, in-flight session photos land last.
  const mergedPhotos = albums.flatMap((a) => {
    if (a.photos && a.photos.length > 0) return a.photos;
    const tile = thumbnailTileFor(a);
    return tile ? [tile] : [];
  });
  const totalPhotos = albums.reduce((sum, a) => sum + effectivePhotoCount(a), 0);
  const uploading = albums.some(
    (a) => a.status === UPLOAD_STATUS.UPLOADING || a.status === UPLOAD_STATUS.PENDING,
  );
  const uploadedPhotos = albums.reduce(
    (sum, a) => sum + (a.status === UPLOAD_STATUS.COMPLETED
      ? effectivePhotoCount(a)
      : (a.uploadedPhotos || 0)),
    0,
  );
  const existingConstituents = albums.filter((a) => a.isExisting);
  const deletableIds = existingConstituents
    .filter((a) => !a.isPublished)
    .map((a) => a.contributionId);
  const anyPublished = existingConstituents.some((a) => a.isPublished);
  const firstThumb = albums.find((a) => a.thumbnail)?.thumbnail || null;
  const albumDate = existingConstituents
    .map((a) => a.createdAt)
    .filter(Boolean)
    .sort()
    .pop() || null;
  // Keep the album's own registered name — prefer a saved backend album's
  // title, falling back to the first album's name. Do NOT rename it.
  const albumName = existingConstituents.find((a) => a.fileName)?.fileName
    || albums.find((a) => a.fileName)?.fileName
    || 'Photo Album';
  const contributorEmail = albums.find((a) => a.contributorEmail)?.contributorEmail || '';

  const mergedAlbum = {
    id: 'merged-album',
    contributionId: 'merged-album',
    type: 'album',
    isPhotoAlbum: true,
    fileName: albumName,
    contributorEmail,
    status: uploading ? UPLOAD_STATUS.UPLOADING : UPLOAD_STATUS.COMPLETED,
    progress: uploading ? Math.round((uploadedPhotos / Math.max(1, totalPhotos)) * 100) : 100,
    photos: mergedPhotos,
    photoCount: totalPhotos,
    totalPhotos,
    uploadedPhotos,
    thumbnail: firstThumb,
    posterUrl: firstThumb,
    isExisting: existingConstituents.length > 0 && !uploading,
    isPublished: existingConstituents.length > 0 && anyPublished && deletableIds.length === 0,
    mergedAlbumIds: deletableIds,
    createdAt: albumDate,
  };
  return [...nonAlbums, mergedAlbum];
};

// ── "Open in app" arrival popup (mirrors the mobile-upload page) ──────────────
// Shown on arrival (mobile only) before the name/email modal, offering to open
// the native Projector app via deep link, with an app-store fallback.
const OPEN_APP_POPUP_TEXT = "Choosing files may take several minutes for it to upload. Please be patient while it's being uploaded. You can upload from the Projector Mobile application. You don't need to sign up to upload images and videos.";
// Pinned yellow banner at the top on mobile (same as the mobile-upload page).
const GALLERY_WARNING_TEXT = "Choosing files may take several minutes for it to upload. Please be patient while it's being uploaded. DO NOT PRESS CANCEL unless you want to stop the upload.";
// ChottuLink smart link used for the "Open in app" hand-off. Building it as a
// plain URL (no API key needed) lets ChottuLink own all the routing:
//   • app installed  → opens projector://contribute?token=... — the app's
//                      existing URI-scheme handling extracts the token
//   • not installed  → App Store (via the App Store ID set in the ChottuLink
//                      dashboard), then the ChottuLink iOS SDK hands the token
//                      to the app on first launch (deferred deep linking)
//   • desktop/no-app → the link's configured web fallback
//                      (https://projector.app/contribute-mobile-upload)
// token + uuid are appended as query params and passed through to the app.
const CHOTTULINK_DOMAIN = 'projectorapp.chottu.link';
const APP_DEEPLINK_PATH = 'contribute'; // → https://projectorapp.chottu.link/contribute?token=...

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
};

const formatTimeRemaining = (seconds) => {
  if (!seconds || seconds <= 0) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const changeSize = (size) => {
  const newSize = size / 1024 / 1024;
  return newSize.toFixed(2);
};

const capitalizeWords = (str) => str.replace(/\b\w/g, (char) => char.toUpperCase());

// ── Capture single video frame (for preview poster) ──────────────────────────
const captureVideoFrame = (file) => new Promise((resolve) => {
  const timeout = setTimeout(() => resolve(null), 6000);
  try {
    const video = document.createElement('video');
    const videoUrl = URL.createObjectURL(file);
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    const cleanup = () => {
      try { URL.revokeObjectURL(videoUrl); } catch (e) { /* ignore */ }
      try { video.remove(); } catch (e) { /* ignore */ }
    };
    video.onloadedmetadata = () => {
      if (video.videoWidth === 0 || video.videoHeight === 0) {
        clearTimeout(timeout); cleanup(); resolve(null); return;
      }
      video.currentTime = 0.5;
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 180;
        const ctx = canvas.getContext('2d');
        if (!ctx) { clearTimeout(timeout); cleanup(); resolve(null); return; }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
        canvas.remove(); clearTimeout(timeout); cleanup(); resolve(dataUrl);
      } catch (e) { clearTimeout(timeout); cleanup(); resolve(null); }
    };
    video.onerror = () => { clearTimeout(timeout); cleanup(); resolve(null); };
    video.src = videoUrl;
    video.load();
  } catch (e) { clearTimeout(timeout); resolve(null); }
});

// ── Generate 6 thumbnails from video (same as gift flow) ─────────────────────
const generateThumbnailsFromVideo = (file) => new Promise((resolve) => {
  if (!file) {
    resolve({
      thumbnails: [], videoHeight: 0, videoWidth: 0, videoDuration: 0,
    });
    return;
  }

  let resolved = false;
  const fallback = {
    thumbnails: [], videoHeight: 0, videoWidth: 0, videoDuration: 0,
  };

  const video = document.createElement('video');
  const videoUrl = URL.createObjectURL(file);

  const cleanup = () => {
    try { URL.revokeObjectURL(videoUrl); } catch (e) { /* ignore */ }
    try { video.remove(); } catch (e) { /* ignore */ }
  };

  const timeout = setTimeout(() => {
    if (!resolved) { resolved = true; cleanup(); resolve(fallback); }
  }, 15000);

  video.preload = 'metadata';
  video.muted = true;
  video.playsInline = true;
  video.src = videoUrl;
  video.load();

  video.onerror = () => {
    if (!resolved) { resolved = true; clearTimeout(timeout); cleanup(); resolve(fallback); }
  };

  video.onloadedmetadata = () => {
    if (resolved) return;
    const { duration, videoHeight, videoWidth } = video;
    if (videoWidth === 0 || videoHeight === 0) {
      resolved = true; clearTimeout(timeout); cleanup(); resolve(fallback); return;
    }

    const thumbnailCount = 6;
    const interval = duration / (thumbnailCount + 1);
    const tempThumbnails = [];
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const maxWidth = 640;
    const scale = Math.min(1, maxWidth / videoWidth);
    canvas.width = videoWidth * scale;
    canvas.height = videoHeight * scale;

    let currentIndex = 0;

    const captureThumbnail = () => {
      if (resolved) return;
      if (currentIndex >= thumbnailCount) {
        resolved = true; clearTimeout(timeout); canvas.remove(); cleanup();
        resolve({
          thumbnails: tempThumbnails, videoHeight, videoWidth, videoDuration: duration,
        });
        return;
      }
      const time = Math.max(0.5, interval * (currentIndex + 1));
      video.currentTime = Math.min(time, duration - 0.1);
    };

    video.onseeked = () => {
      if (resolved) return;
      requestAnimationFrame(() => {
        if (resolved) return;
        try {
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          tempThumbnails.push(canvas.toDataURL('image/jpeg', 0.7));
        } catch (e) { /* skip frame */ }
        currentIndex += 1;
        captureThumbnail();
      });
    };

    captureThumbnail();
  };
});

// ── Convert base64 to File for upload ────────────────────────────────────────
const base64ToFile = (base64String, fileName) => {
  const [header, base64Data] = base64String.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(base64Data);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) array[i] = binary.charCodeAt(i);
  return new File([array], fileName, { type: mime });
};

// ── Upload a single small file via gift session (1 part) ─────────────────────
const uploadSingleFileGift = async (uuid, file, contentType) => {
  const session = await createGiftUploadSession(uuid, {
    file_name: file.name,
    file_size: file.size,
    content_type: contentType || file.type || 'application/octet-stream',
  });

  const presignedUrl = session.presigned_urls?.[0]?.presigned_url;
  if (!presignedUrl) throw new Error('No presigned URL returned');

  const partResult = await uploadPartWithPresignedUrl(presignedUrl, file, 1);
  const completeResponse = await completeGiftUploadSession(session.id, uuid, [partResult]);
  return completeResponse?.s3_key || completeResponse?.file_key || session.s3_key || session.file_key;
};

// ════════════════════════════════════════════════════════════════════════════════
// Component
// ════════════════════════════════════════════════════════════════════════════════
function ContributePage() {
  const [searchParams] = useSearchParams();
  const tempToken = searchParams.get('token') || '';

  const { setAppSnackbar } = useContext(AppContext);

  // ── UUID and user info from getLinkInfo ────────────────────────────────────
  const [uuid, setUuid] = useState('');
  const [linkError, setLinkError] = useState('');
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');

  useEffect(() => {
    if (!tempToken) { setLinkError('No token provided'); return; }
    (async () => {
      try {
        const res = await getContributionLinkInfo({ temp_token: tempToken });
        const data = res?.data;
        if (!data?.success) { setLinkError('Invalid or expired link'); return; }
        if (data.is_expired) { setLinkError('This shared folder link has expired'); return; }
        if (!data.is_active) { setLinkError('This shared folder link is no longer active'); return; }

        setUuid(data.uuid);
        setOwnerFirstName(data.user_firstname || '');
        setOwnerEmail(data.user_email || '');
      } catch (err) {
        console.error('[Contribute] getLinkInfo error:', err);
        setLinkError('Failed to validate link');
      }
    })();
  }, [tempToken]);

  // Store temp_token separately — do NOT set it as main 'token'
  // because app.jsx autoSignIn calls getMyProfile with it, fails, and reloads the page in a loop
  useEffect(() => {
    localStorage.removeItem('contributorsName');
  }, []);

  // ── "Open in app" arrival popup (mobile only) ──────────────────────────────
  // Shows before the name/email modal, matching the mobile-upload page. On
  // desktop the deep link is useless, so we skip straight to the name/email step.
  const isMobileDevice = /iphone|ipad|ipod|android/i.test(navigator.userAgent || '');
  const [showAppPopup, setShowAppPopup] = useState(isMobileDevice);

  // Hand off to the native app via a ChottuLink smart link. ChottuLink owns all
  // the routing (open app / App Store + deferred deep link / web fallback), so
  // we no longer build the raw projector:// URL or the visibilitychange + store
  // fallback by hand. token + uuid are forwarded so the app lands in the right
  // shared-folder session — even after a fresh install (deferred deep linking).
  const handleOpenApp = () => {
    const params = new URLSearchParams();
    if (tempToken) params.set('token', tempToken);
    if (uuid) params.set('uuid', uuid);
    const query = params.toString();
    const smartLink = `https://${CHOTTULINK_DOMAIN}/${APP_DEEPLINK_PATH}${query ? `?${query}` : ''}`;
    window.location.href = smartLink;
  };

  // ── Contributor popup state ────────────────────────────────────────────────
  const [showContributorPopup, setShowContributorPopup] = useState(true);
  const [contributorName, setContributorName] = useState('');
  const [contributorEmail, setContributorEmail] = useState('');
  const [contNameTouched, setContNameTouched] = useState(false);
  const [contNameValidReady, setContNameValidReady] = useState(false);
  const [contEmailTouched, setContEmailTouched] = useState(false);
  const [contEmailValidReady, setContEmailValidReady] = useState(false);

  // ── Returning contributor: manage own previously-uploaded files ──
  // Scoped server-side to this link (temp_token) + the entered email/phone.
  const [myUploads, setMyUploads] = useState([]);
  const [myUploadsLoading, setMyUploadsLoading] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const contTrimmedName = contributorName.trim();
  const contNameNoSpaces = contTrimmedName.replace(/\s/g, '');
  const contHasSpace = contTrimmedName.includes(' ');
  const contFullNameValid = contNameNoSpaces.length >= 4 && contHasSpace;
  // Accept either an email OR a phone number. Whatever is entered is still
  // sent in the same `email` payload field (no backend change).
  const contContactValue = contributorEmail.trim();
  const contIsEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contContactValue);
  const contPhoneDigits = contContactValue.replace(/\D/g, '');
  const contIsPhone = /^\+?[\d\s\-()]+$/.test(contContactValue)
    && contPhoneDigits.length >= 7 && contPhoneDigits.length <= 15;
  const contEmailValid = !!contContactValue && (contIsEmail || contIsPhone);

  useEffect(() => {
    if (!contNameTouched) return undefined;
    setContNameValidReady(false);
    const timer = setTimeout(() => setContNameValidReady(true), 1200);
    return () => clearTimeout(timer);
  }, [contributorName, contNameTouched]);

  useEffect(() => {
    if (!contEmailTouched) return undefined;
    setContEmailValidReady(false);
    const timer = setTimeout(() => setContEmailValidReady(true), 1200);
    return () => clearTimeout(timer);
  }, [contributorEmail, contEmailTouched]);

  const handleContributorContinue = () => {
    localStorage.setItem('contributorsName', contributorName.trim());
    setShowContributorPopup(false);
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleContributorKeyDown = (e) => {
    if (e.key === 'Enter' && contFullNameValid && contEmailValid) {
      handleContributorContinue();
    }
  };

  // Fetch the contributor's own prior uploads for this link. No-ops until we
  // have a link (uuid + token) and a valid email/phone.
  const fetchMyUploads = useCallback(async () => {
    const email = contributorEmail.trim();
    if (!uuid || !tempToken || !email || !contEmailValid) return;
    setMyUploadsLoading(true);
    try {
      const res = await listMyContributions({ temp_token: tempToken, email });
      setMyUploads(res?.data?.success ? (res.data.contributions || []) : []);
    } catch (err) {
      setMyUploads([]);
    } finally {
      setMyUploadsLoading(false);
    }
  }, [uuid, tempToken, contributorEmail, contEmailValid]);

  // Load the list once the contributor has confirmed their identity (popup
  // dismissed) and we have a valid email/phone.
  useEffect(() => {
    if (showContributorPopup || !contEmailValid || !uuid || !tempToken) return;
    fetchMyUploads();
  }, [showContributorPopup, contEmailValid, uuid, tempToken, fetchMyUploads]);

  // Delete one of the contributor's own, not-yet-published uploads. The
  // server re-checks ownership + published state; on a published/none race
  // we just re-sync the list so the UI reflects reality.
  const handleDeleteMyUpload = useCallback(async (contributionId) => {
    // Keep the confirm row mounted (don't clear confirmDeleteId yet) so its
    // Delete button can show the in-progress spinner.
    setDeletingId(contributionId);
    try {
      const res = await deleteMyContribution({
        temp_token: tempToken,
        email: contributorEmail.trim(),
        contribution_id: contributionId,
      });
      if (res?.data?.success) {
        setMyUploads((prev) => prev.filter((c) => c.id !== contributionId));
      } else {
        await fetchMyUploads();
      }
    } catch (err) {
      // 409 already_published / 404 not_found → re-sync to show real state.
      await fetchMyUploads();
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  }, [tempToken, contributorEmail, fetchMyUploads]);

  // Delete the aggregated "All photos" album card: removes every underlying
  // not-yet-published album contribution it represents, then re-syncs.
  const handleDeleteMergedAlbum = useCallback(async (contributionIds) => {
    if (!contributionIds || contributionIds.length === 0) return;
    setDeletingId('merged-album');
    try {
      await Promise.all(contributionIds.map((cid) => deleteMyContribution({
        temp_token: tempToken,
        email: contributorEmail.trim(),
        contribution_id: cid,
      }).catch(() => null)));
      await fetchMyUploads();
    } finally {
      setDeletingId(null);
      setConfirmDeleteId(null);
    }
  }, [tempToken, contributorEmail, fetchMyUploads]);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const filesInputRef = useRef(null);
  const fileDataRef = useRef(new Map());

  // ── File selection & upload state (declared early so SSE handler can access) ──
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploads, setUploads] = useState([]);
  // Level-2 (inside-a-folder) sub-tab: 'all' | 'videos' | 'photos'.
  const [contentFilter, setContentFilter] = useState('all');
  // Level-1 top tab: 'folders' | 'videos' | 'photos' (mobile parity).
  const [topTab, setTopTab] = useState('folders');
  // Key (lowercased contributor email) of the folder the contributor has
  // drilled into; null at the top level.
  const [openFolderKey, setOpenFolderKey] = useState(null);
  const [showAddMore, setShowAddMore] = useState(false);
  // Only the setter is used (upload state is reflected per-card now).
  const [, setIsUploading] = useState(false);
  // ── Owner plan gating ──────────────────────────────────────────────────────
  // Free plan (subscription null or id '6') caps the owner's account at 15
  // items; other plans are unlimited. Fetched once the link resolves. When
  // limited, a contributor can add at most `ownerRemainingSlots` more items.
  const [ownerPlanLimited, setOwnerPlanLimited] = useState(false);
  const [ownerRemainingSlots, setOwnerRemainingSlots] = useState(FREE_PLAN_ITEM_LIMIT);
  const [limitError, setLimitError] = useState('');
  // Red inline message shown when a Free-plan owner can't accept the selection.
  const limitMessage = useCallback((remaining) => {
    const who = ownerFirstName || 'This user';
    if (remaining <= 0) {
      return `${who} is on the Free plan and can't receive more files. Please ask them to upgrade to upload more.`;
    }
    return `${who} is on the Free plan — you can add only ${remaining} more ${remaining === 1 ? 'item' : 'items'} (photos and videos). Please ask them to upgrade to upload more.`;
  }, [ownerFirstName]);
  const uploadsRef = useRef([]);
  const queuedFileIdsRef = useRef(new Set());
  const photoAlbumIdRef = useRef(null);
  const trackedMobilePhotoUidsRef = useRef(new Set());
  const uploadAlbumRef = useRef(null);
  useEffect(() => { uploadsRef.current = uploads; }, [uploads]);
  // Latest fetched contributions — used as a baseline count when reconciling a
  // finished batch (see reconcileAfterUpload).
  const myUploadsRef = useRef([]);
  useEffect(() => { myUploadsRef.current = myUploads; }, [myUploads]);

  // Fetch the owner's plan. Free (subscription null or id '6') → 15-item
  // account cap, so a contributor may add at most (15 - already uploaded) more
  // items; any other plan is unlimited. On a network error we leave it
  // unlimited rather than falsely block a contributor. Re-run after a batch
  // finishes so `total_content_uploaded` (and the remaining slots) stay fresh.
  const fetchOwnerPlan = useCallback(async () => {
    if (!uuid || !tempToken) return;
    try {
      const res = await getContributionSubscription({ temp_token: tempToken, uuid });
      const data = res?.data;
      if (!data?.success) return;
      const sub = data.subscription;
      const uploaded = Number(data.total_content_uploaded) || 0;
      const isLimited = !sub || String(sub.id) === FREE_PLAN_ID;
      setOwnerPlanLimited(isLimited);
      setOwnerRemainingSlots(Math.max(0, FREE_PLAN_ITEM_LIMIT - uploaded));
    } catch (err) {
      console.error('[Contribute] getSubscription error:', err);
    }
  }, [uuid, tempToken]);

  useEffect(() => { fetchOwnerPlan(); }, [fetchOwnerPlan]);

  // ── QR flip state ──────────────────────────────────────────────────────────
  const [showQrCode, setShowQrCode] = useState(false);
  // ── Album photos modal for grid view ──────────────────────────────────────
  const [albumModal, setAlbumModal] = useState(null);

  // ── Contribute mobile SSE (token-based, same as mobile upload in app.jsx) ──
  const contributeSSERef = useRef(null);
  const processedContributeMessages = useRef(new Set());
  // Cache filename→thumbnail so it survives state churn (same pattern as mobileThumbCacheRef in app.jsx)
  const mobileThumbCacheRef = useRef({});
  const [sseSessionId, setSseSessionId] = useState(null);

  // Create SSE session on mount (public — no auth needed)
  useEffect(() => {
    if (!uuid || !tempToken) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await createUploadSSESessionPublic(tempToken);
        const id = response?.data?.session_id;
        if (!cancelled && id) {
          setSseSessionId(id);
        }
      } catch (err) {
        console.error('[Contribute SSE] Failed to create session:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [uuid, tempToken]);

  // SSE message handler — mirrors handleMobileUploadSSEMessage in app.jsx
  const handleContributeSSEMessage = useCallback((data) => {
    const statusData = data.parsedStatusData || data.status_data;
    if (!statusData) return;

    let parsed;
    try {
      parsed = typeof statusData === 'string' ? JSON.parse(statusData) : statusData;
    } catch (e) { return; }

    const { action, fileId, fileName } = parsed;

    // Dedup: prevent processing same message twice (SSE replays on reconnect)
    const filesKey = parsed.files && Array.isArray(parsed.files) ? parsed.files.sort().join(',') : '';
    const messageId = `${action}_${fileId || ''}_${filesKey}_${data.timestamp || ''}`;
    if (processedContributeMessages.current.has(messageId)) return;
    processedContributeMessages.current.add(messageId);
    if (processedContributeMessages.current.size > 100) {
      const items = Array.from(processedContributeMessages.current);
      processedContributeMessages.current = new Set(items.slice(-100));
    }

    console.log('[Contribute SSE] Processing:', action, '| fileId:', fileId, '| fileName:', fileName);

    // fileUpload — files selected on mobile (batch notification)
    if (action && action.startsWith('fileUpload') && parsed.files && Array.isArray(parsed.files)) {
      const thumbMap = parsed.thumbs || {};
      // Cache thumbnails so they survive state updates
      Object.entries(thumbMap).forEach(([name, thumb]) => {
        if (thumb) mobileThumbCacheRef.current[name] = thumb;
      });

      const newMobileVideoUploads = [];
      let hasNewMobilePhotos = false;
      parsed.files.forEach((entry) => {
        const pipeIdx = entry.lastIndexOf('|');
        const fName = pipeIdx > 0 ? entry.substring(0, pipeIdx) : entry;
        const fId = pipeIdx > 0 ? entry.substring(pipeIdx + 1) : `batch-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
        const thumbForFile = thumbMap[fName] || mobileThumbCacheRef.current[fName] || null;
        const ext = `.${(fName || '').split('.').pop().toLowerCase()}`;
        const isImage = IMAGE_EXTENSIONS.includes(ext);

        if (isImage) {
          // Track mobile photo UIDs for merging into album
          if (!trackedMobilePhotoUidsRef.current.has(fId)) {
            trackedMobilePhotoUidsRef.current.add(fId);
            hasNewMobilePhotos = true;
            // Store thumbnail in cache keyed by UID
            if (thumbForFile) mobileThumbCacheRef.current[`uid_${fId}`] = thumbForFile;
          }
        } else {
          newMobileVideoUploads.push({
            id: `mobile-${fId}`,
            mobileUid: fId,
            fileName: fName,
            fileSize: 0,
            status: UPLOAD_STATUS.UPLOADING,
            progress: 0,
            bytesUploaded: 0,
            speed: 0,
            timeRemaining: 0,
            type: 'video',
            isMobileUpload: true,
            thumbnail: thumbForFile,
          });
        }
      });

      // Add video entries
      if (newMobileVideoUploads.length > 0) {
        setUploads((prev) => {
          const existingIds = new Set(prev.map((u) => u.mobileUid));
          const toAdd = newMobileVideoUploads.filter((u) => !existingIds.has(u.mobileUid));
          return toAdd.length > 0 ? [...toAdd, ...prev] : prev;
        });
        // Also add to selectedFiles so the files container becomes visible
        // Mark as already queued so auto-start useEffect doesn't try to upload them
        newMobileVideoUploads.forEach((u) => queuedFileIdsRef.current.add(u.id));
        setSelectedFiles((prev) => {
          const existingIds = new Set(prev.map((f) => f.id));
          const toAdd = newMobileVideoUploads
            .filter((u) => !existingIds.has(u.id))
            .map((u) => ({
              id: u.id,
              name: u.fileName,
              size: 0,
              type: 'video',
              source: 'mobile',
            }));
          return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
        });
      }

      // Merge mobile photos into album entry
      if (hasNewMobilePhotos) {
        const totalMobilePhotos = trackedMobilePhotoUidsRef.current.size;
        const mobilePhotoEntries = Array.from(trackedMobilePhotoUidsRef.current).map((uid) => ({
          id: `mobile-photo-${uid}`,
          name: uid,
          thumbnailUrl: mobileThumbCacheRef.current[`uid_${uid}`] || null,
          isMobile: true,
        }));

        setUploads((prev) => {
          const existingAlbum = prev.find((u) => u.isPhotoAlbum);
          if (existingAlbum) {
            // Merge mobile photos into existing album
            const desktopPhotos = (existingAlbum.photos || []).filter((p) => !p.isMobile);
            const mergedPhotos = [...desktopPhotos, ...mobilePhotoEntries];
            const baseDesktop = existingAlbum.baseDesktopPhotos ?? desktopPhotos.length;
            const newTotal = baseDesktop + totalMobilePhotos;
            return prev.map((u) => (u.id === existingAlbum.id ? {
              ...u,
              totalPhotos: newTotal,
              mobilePhotoCount: totalMobilePhotos,
              photoCount: newTotal,
              fileName: `${ALL_PHOTOS_ALBUM_NAME} (${newTotal} photos)`,
              photos: mergedPhotos,
            } : u));
          }
          // Create new mobile-only album entry
          return [{
            id: `album-mobile-${Date.now()}`,
            fileName: `${ALL_PHOTOS_ALBUM_NAME} (${totalMobilePhotos} photos)`,
            fileSize: 0,
            status: UPLOAD_STATUS.UPLOADING,
            progress: 0,
            bytesUploaded: 0,
            speed: 0,
            timeRemaining: 0,
            type: 'album',
            isPhotoAlbum: true,
            isMobileUpload: true,
            totalPhotos: totalMobilePhotos,
            mobilePhotoCount: totalMobilePhotos,
            baseDesktopPhotos: 0,
            photoCount: totalMobilePhotos,
            photos: mobilePhotoEntries,
          }, ...prev];
        });

        // Also add to selectedFiles so handleStartUpload picks it up
        setSelectedFiles((prev) => {
          const existingAlbum = prev.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME);
          if (existingAlbum) return prev; // album already tracked
          return [...prev, {
            id: `album-mobile-${Date.now()}`,
            name: ALL_PHOTOS_ALBUM_NAME,
            type: 'album',
            source: 'mobile',
            size: 0,
            photoCount: totalMobilePhotos,
            photos: mobilePhotoEntries,
          }];
        });
      }

      setShowQrCode(false);
      setShowAddMore(false);
      return;
    }

    switch (action) {
      case 'fileComplete': {
        // Cumulative file list — files[] contains ALL completed files so far
        const completedFiles = parsed.files || [];
        const s3KeysMap = parsed.s3Keys || {};
        const thumbnailKeysMap = parsed.thumbnailKeys || {};
        const s3Base = generateS3Url();

        setUploads((prev) => {
          let updated = [...prev];
          let mobilePhotosCompleted = 0;
          completedFiles.forEach((entry) => {
            const pipeIdx = entry.lastIndexOf('|');
            const fName = pipeIdx > 0 ? entry.substring(0, pipeIdx) : entry;
            const fId = pipeIdx > 0 ? entry.substring(pipeIdx + 1) : null;
            if (!fId) return;

            // If this is a tracked mobile photo, update the album instead
            const ext = `.${(fName || '').split('.').pop().toLowerCase()}`;
            if (IMAGE_EXTENSIONS.includes(ext) || trackedMobilePhotoUidsRef.current.has(fId)) {
              trackedMobilePhotoUidsRef.current.add(fId);
              mobilePhotosCompleted += 1;
              return;
            }

            const thumbUrl = thumbnailKeysMap[fId] ? `${s3Base}${thumbnailKeysMap[fId]}` : null;
            const existing = updated.find((u) => u.mobileUid === fId);
            if (!existing) {
              // Recovery: create entry if missed the fileUpload message
              updated = [{
                id: `mobile-${fId}`,
                mobileUid: fId,
                fileName: fName,
                fileSize: 0,
                status: UPLOAD_STATUS.COMPLETED,
                progress: 100,
                bytesUploaded: 0,
                speed: 0,
                timeRemaining: 0,
                type: 'video',
                isMobileUpload: true,
                thumbnail: thumbUrl,
                s3Key: s3KeysMap[fId] || null,
              }, ...updated];
            } else if (existing.status !== UPLOAD_STATUS.COMPLETED) {
              updated = updated.map((u) => (u.mobileUid === fId
                ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, s3Key: s3KeysMap[fId] || u.s3Key, thumbnail: thumbUrl || u.thumbnail }
                : u));
            }
          });
          // Update mobile photo completion count AND rebuild album photos from ref
          // (safety net: even if fileUpload SSE was missed, fileComplete carries UIDs)
          if (mobilePhotosCompleted > 0) {
            const totalMobilePhotos = trackedMobilePhotoUidsRef.current.size;
            const mobilePhotoEntries = Array.from(trackedMobilePhotoUidsRef.current).map((uid) => ({
              id: `mobile-photo-${uid}`,
              name: uid,
              thumbnailUrl: mobileThumbCacheRef.current[`uid_${uid}`] || null,
              isMobile: true,
            }));
            updated = updated.map((u) => {
              if (!u.isPhotoAlbum) return u;
              const desktopPhotos = (u.photos || []).filter((p) => !p.isMobile);
              const mergedPhotos = [...desktopPhotos, ...mobilePhotoEntries];
              const baseDesktop = u.baseDesktopPhotos ?? desktopPhotos.length;
              const newTotal = baseDesktop + totalMobilePhotos;
              const newCompleted = Math.max(u.mobilePhotosCompleted || 0, mobilePhotosCompleted);
              return {
                ...u,
                mobilePhotosCompleted: newCompleted,
                totalPhotos: newTotal,
                mobilePhotoCount: totalMobilePhotos,
                photoCount: newTotal,
                fileName: `${ALL_PHOTOS_ALBUM_NAME} (${newTotal} photos)`,
                photos: mergedPhotos,
              };
            });
          }
          return updated;
        });
        break;
      }
      case 'allComplete': {
        const allFiles = parsed.files || [];
        const allS3Keys = parsed.s3Keys || {};
        const allThumbnailKeys = parsed.thumbnailKeys || {};
        const s3BaseAll = generateS3Url();

        // Cache any photo thumbs sent as fallback in allComplete
        const allThumbMap = parsed.thumbs || {};
        Object.entries(allThumbMap).forEach(([name, thumb]) => {
          if (thumb) mobileThumbCacheRef.current[name] = thumb;
        });

        // Track any photo UIDs from the cumulative file list (safety net for missed fileUpload)
        allFiles.forEach((entry) => {
          const pipeIdx = entry.lastIndexOf('|');
          const fName = pipeIdx > 0 ? entry.substring(0, pipeIdx) : entry;
          const fId = pipeIdx > 0 ? entry.substring(pipeIdx + 1) : null;
          if (!fId) return;
          const ext = `.${(fName || '').split('.').pop().toLowerCase()}`;
          if (IMAGE_EXTENSIONS.includes(ext)) {
            if (!trackedMobilePhotoUidsRef.current.has(fId)) {
              trackedMobilePhotoUidsRef.current.add(fId);
              const thumbForFile = allThumbMap[fName] || mobileThumbCacheRef.current[fName] || null;
              if (thumbForFile) mobileThumbCacheRef.current[`uid_${fId}`] = thumbForFile;
            }
          }
        });

        setUploads((prev) => {
          let updated = [...prev];

          // Create any missing video entries from cumulative file list (safety net)
          allFiles.forEach((entry) => {
            const pipeIdx = entry.lastIndexOf('|');
            const fName = pipeIdx > 0 ? entry.substring(0, pipeIdx) : entry;
            const fId = pipeIdx > 0 ? entry.substring(pipeIdx + 1) : null;
            if (!fId) return;

            // Skip photos — they are handled via the album entry below
            const ext = `.${(fName || '').split('.').pop().toLowerCase()}`;
            if (IMAGE_EXTENSIONS.includes(ext) || trackedMobilePhotoUidsRef.current.has(fId)) {
              return;
            }

            const thumbUrl = allThumbnailKeys[fId] ? `${s3BaseAll}${allThumbnailKeys[fId]}` : null;
            const existing = updated.find((u) => u.mobileUid === fId);
            if (!existing) {
              updated = [{
                id: `mobile-${fId}`,
                mobileUid: fId,
                fileName: fName,
                fileSize: 0,
                status: UPLOAD_STATUS.COMPLETED,
                progress: 100,
                bytesUploaded: 0,
                speed: 0,
                timeRemaining: 0,
                type: 'video',
                isMobileUpload: true,
                thumbnail: thumbUrl,
                s3Key: allS3Keys[fId] || null,
              }, ...updated];
            } else if (existing.status !== UPLOAD_STATUS.COMPLETED) {
              updated = updated.map((u) => (u.mobileUid === fId
                ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, s3Key: allS3Keys[fId] || u.s3Key, thumbnail: thumbUrl || u.thumbnail }
                : u));
            }
          });

          // Rebuild album photos from trackedMobilePhotoUidsRef (safety net for missed fileUpload SSEs)
          const totalMobilePhotos = trackedMobilePhotoUidsRef.current.size;
          if (totalMobilePhotos > 0) {
            const mobilePhotoEntries = Array.from(trackedMobilePhotoUidsRef.current).map((uid) => ({
              id: `mobile-photo-${uid}`,
              name: uid,
              thumbnailUrl: mobileThumbCacheRef.current[`uid_${uid}`] || null,
              isMobile: true,
            }));

            const existingAlbum = updated.find((u) => u.isPhotoAlbum);
            if (existingAlbum) {
              const desktopPhotos = (existingAlbum.photos || []).filter((p) => !p.isMobile);
              const mergedPhotos = [...desktopPhotos, ...mobilePhotoEntries];
              const baseDesktop = existingAlbum.baseDesktopPhotos ?? desktopPhotos.length;
              const newTotal = baseDesktop + totalMobilePhotos;
              updated = updated.map((u) => (u.id === existingAlbum.id ? {
                ...u,
                mobilePhotosCompleted: totalMobilePhotos,
                totalPhotos: newTotal,
                mobilePhotoCount: totalMobilePhotos,
                photoCount: newTotal,
                fileName: `${ALL_PHOTOS_ALBUM_NAME} (${newTotal} photos)`,
                photos: mergedPhotos,
                status: UPLOAD_STATUS.COMPLETED,
                progress: 100,
              } : u));
            } else {
              // Create album if none exists (all fileUpload messages were missed)
              updated = [{
                id: `album-mobile-${Date.now()}`,
                fileName: `${ALL_PHOTOS_ALBUM_NAME} (${totalMobilePhotos} photos)`,
                fileSize: 0,
                status: UPLOAD_STATUS.COMPLETED,
                progress: 100,
                bytesUploaded: 0,
                speed: 0,
                timeRemaining: 0,
                type: 'album',
                isPhotoAlbum: true,
                isMobileUpload: true,
                totalPhotos: totalMobilePhotos,
                mobilePhotoCount: totalMobilePhotos,
                mobilePhotosCompleted: totalMobilePhotos,
                baseDesktopPhotos: 0,
                photoCount: totalMobilePhotos,
                photos: mobilePhotoEntries,
              }, ...updated];
            }
          }

          // Mark any remaining mobile entries as complete (videos)
          updated = updated.map((u) => {
            if (u.isMobileUpload && u.status !== UPLOAD_STATUS.COMPLETED) {
              return { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100, s3Key: allS3Keys[u.mobileUid] || u.s3Key };
            }
            return u;
          });

          return updated;
        });
        break;
      }
      default:
        break;
    }

    // After SSE updates, check if all uploads are now done and reset isUploading
    setTimeout(() => {
      const hasActive = uploadsRef.current.some(
        (u) => u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PAUSED || u.status === UPLOAD_STATUS.PENDING,
      );
      if (!hasActive && uploadsRef.current.length > 0) setIsUploading(false);
    }, 100);
  }, []);

  // Subscribe to mobile upload SSE using session_id + tempToken
  useEffect(() => {
    if (!sseSessionId || !tempToken) return undefined;
    if (contributeSSERef.current) {
      contributeSSERef.current();
      contributeSSERef.current = null;
    }
    processedContributeMessages.current.clear();

    const cleanup = subscribeToUploadStatus(sseSessionId, tempToken, {
      onOpen: () => { console.log('[Contribute SSE] Connected | session:', sseSessionId); },
      onMessage: handleContributeSSEMessage,
      onError: (error) => { console.error('[Contribute SSE] Error:', error); },
    });
    contributeSSERef.current = cleanup;

    return () => {
      if (contributeSSERef.current) {
        contributeSSERef.current();
        contributeSSERef.current = null;
      }
    };
  }, [sseSessionId, tempToken, handleContributeSSEMessage]);

  // Clean up SSE on unmount
  useEffect(() => () => {
    if (contributeSSERef.current) {
      contributeSSERef.current();
      contributeSSERef.current = null;
    }
  }, []);

  // Build QR URL (includes session for mobile SSE sending)
  const contNameEncoded = encodeURIComponent(contributorName.trim());
  const contEmailEncoded = encodeURIComponent(contributorEmail.trim());
  const contributeQrUrl = `${window.location.origin}/contribute-mobile-upload?token=${tempToken}&uuid=${uuid}${sseSessionId ? `&session=${sseSessionId}` : ''}${contNameEncoded ? `&name=${contNameEncoded}` : ''}${contEmailEncoded ? `&email=${contEmailEncoded}` : ''}`;

  // Copy-to-clipboard for the QR link (the full URL — shortening was dropped
  // because /shortLink doesn't accept the shared-folder temp token).
  const [linkCopied, setLinkCopied] = useState(false);

  const handleCopyQrLink = async () => {
    if (!contributeQrUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(contributeQrUrl);
      } else {
        // Fallback for insecure contexts / older browsers.
        const temp = document.createElement('textarea');
        temp.value = contributeQrUrl;
        temp.style.position = 'fixed';
        temp.style.opacity = '0';
        document.body.appendChild(temp);
        temp.select();
        document.execCommand('copy');
        document.body.removeChild(temp);
      }
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.warn('[Contribute] Copy failed:', err);
    }
  };

  // ── Cloud modal state ──────────────────────────────────────────────────────
  const [cloudModalOpen, setCloudModalOpen] = useState(false);
  const [selectedCloudService, setSelectedCloudService] = useState(null);

  useEffect(() => {
    const svc = sessionStorage.getItem('autoOpenCloudModal');
    if (svc) {
      sessionStorage.removeItem('autoOpenCloudModal');
      setSelectedCloudService(svc);
      setCloudModalOpen(true);
    }
  }, []);

  const handleCloudConnect = (service) => {
    sessionStorage.setItem('uploadDropdownCloudConnect', service);
    sessionStorage.setItem('oauthReturnUrl', `/contribute${window.location.search}`);
    setSelectedCloudService(service);
    setCloudModalOpen(true);
  };

  const handleCloudModalClose = () => { setCloudModalOpen(false); setSelectedCloudService(null); };

  // ── Subscribe to uploadManagerSecure for video progress ────────────────────
  useEffect(() => {
    const unsubscribe = uploadManagerSecure.subscribe(({ uploads: managerUploads }) => {
      if (managerUploads.length === 0) return;
      setUploads((prev) => {
        let changed = false;
        const updated = prev.map((upload) => {
          if (!upload.secureUploadId) return upload;
          const mu = managerUploads.find((u) => u.id === upload.secureUploadId);
          if (!mu) return upload;

          let mappedStatus = upload.status;
          if (mu.status === 'pending') mappedStatus = UPLOAD_STATUS.PENDING;
          else if (mu.status === 'uploading' || mu.status === 'initializing' || mu.status === 'finishing') mappedStatus = UPLOAD_STATUS.UPLOADING;
          else if (mu.status === 's3_complete' || mu.status === 'completed') mappedStatus = UPLOAD_STATUS.COMPLETED;
          else if (mu.status === 'paused') mappedStatus = UPLOAD_STATUS.PAUSED;
          else if (mu.status === 'failed') mappedStatus = UPLOAD_STATUS.FAILED;
          else if (mu.status === 'cancelled') mappedStatus = UPLOAD_STATUS.CANCELLED;
          else if (mu.status === 'network_error') mappedStatus = UPLOAD_STATUS.NETWORK_ERROR;

          const managerProgress = mu.status === 's3_complete' ? 100 : Math.round((mu.progress || 0) * 0.9);
          changed = true;
          return {
            ...upload,
            status: mappedStatus,
            progress: Math.max(managerProgress, upload.progress || 0),
            bytesUploaded: Math.max(mu.bytesUploaded || 0, upload.bytesUploaded || 0),
            speed: mu.speed || upload.speed,
            timeRemaining: mu.timeRemaining || upload.timeRemaining,
            s3Key: mu.s3Key || upload.s3Key,
          };
        });
        return changed ? updated : prev;
      });
    });
    return unsubscribe;
  }, []);

  // ── Cloud file download & add to selectedFiles ─────────────────────────────
  const handleCloudFilesSelected = async (cloudFiles, service) => {
    if (cloudFiles.length === 0) return;
    handleCloudModalClose();

    // Get cloud access token
    const cloudServices = localStorage.getItem('cloudConnectedServices');
    const cloudAccessToken = cloudServices ? JSON.parse(cloudServices)[service]?.accessToken : null;
    if (!cloudAccessToken) {
      setAppSnackbar({ isVisible: true, message: 'Cloud service not connected', type: 'error' });
      return;
    }

    const videoFiles = cloudFiles.filter((f) => f.fileType === 'video');
    const imageFiles = cloudFiles.filter((f) => f.fileType === 'image');

    // Owner plan gating (Free plan → 15-item cap): reject the whole cloud
    // selection up front, same as local file picking, so nothing is added.
    if (ownerPlanLimited) {
      const wouldBeItems = countSelectedItems(selectedFiles) + videoFiles.length + imageFiles.length;
      if (wouldBeItems > ownerRemainingSlots) {
        setLimitError(limitMessage(ownerRemainingSlots));
        return;
      }
    }
    setLimitError('');

    // Download a single cloud file and return a local File object
    const downloadCloudFile = async (file) => {
      let tempUrl = null;
      let headers = {};

      if (service === 'dropbox') {
        const dropboxHeaders = {
          Authorization: `Bearer ${cloudAccessToken}`,
          'Content-Type': 'application/json',
        };
        if (file.namespaceId) {
          dropboxHeaders['Dropbox-API-Path-Root'] = JSON.stringify({
            '.tag': 'namespace_id',
            namespace_id: file.namespaceId,
          });
        }
        const response = await AXIOS.post(
          'https://api.dropboxapi.com/2/files/get_temporary_link',
          { path: file.id },
          { headers: dropboxHeaders },
        );
        tempUrl = response.data.link;
      } else if (service === 'googledrive') {
        tempUrl = `https://www.googleapis.com/drive/v3/files/${file.id}?alt=media`;
        headers = { Authorization: `Bearer ${cloudAccessToken}` };
      }

      if (!tempUrl) throw new Error(`Unsupported cloud service: ${service}`);

      const downloadResponse = await fetch(tempUrl, { headers });
      if (!downloadResponse.ok) throw new Error(`Failed to download ${file.name}`);
      const blob = await downloadResponse.blob();
      return new File([blob], file.name, {
        type: file.mimeType || blob.type || 'application/octet-stream',
      });
    };

    // Download and add video files
    for (let i = 0; i < videoFiles.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const fileObj = await downloadCloudFile(videoFiles[i]);
        const videoItem = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          file: fileObj,
          name: fileObj.name,
          size: fileObj.size,
          type: 'video',
          source: 'cloud',
          previewUrl: URL.createObjectURL(fileObj),
        };
        setSelectedFiles((prev) => [...prev, videoItem]);

        // Generate poster thumbnail in background
        (async () => {
          const poster = await captureVideoFrame(fileObj);
          if (poster) {
            const vid = videoItem;
            setSelectedFiles((prev) => prev.map((f) => (f.id === vid.id ? { ...f, posterUrl: poster } : f)));
            setUploads((prev) => prev.map((u) => (u.id === vid.id && !u.posterUrl ? { ...u, posterUrl: poster } : u)));
          }
        })();
      } catch (err) {
        console.error('[Contribute] Cloud video download error:', err);
        setAppSnackbar({ isVisible: true, message: `Failed to download ${videoFiles[i].name}`, type: 'error' });
      }
    }

    // Download and add image files as an album
    if (imageFiles.length > 0) {
      const downloadedPhotos = [];
      for (let i = 0; i < imageFiles.length; i += 1) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const fileObj = await downloadCloudFile(imageFiles[i]);
          downloadedPhotos.push({
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            file: fileObj,
            name: fileObj.name,
            size: fileObj.size,
            previewUrl: URL.createObjectURL(fileObj),
          });
        } catch (err) {
          console.error('[Contribute] Cloud photo download error:', err);
        }
      }
      if (downloadedPhotos.length > 0) {
        const existingAlbumEntry = selectedFiles.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME);
        const albumAlreadyQueued = existingAlbumEntry && queuedFileIdsRef.current.has(existingAlbumEntry.id);

        setSelectedFiles((prev) => {
          const existingAlbum = prev.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME);
          if (existingAlbum) {
            const mergedPhotos = [...(existingAlbum.photos || []), ...downloadedPhotos];
            const mergedSize = mergedPhotos.reduce((sum, p) => sum + p.size, 0);
            return prev.map((f) => (f.id === existingAlbum.id ? {
              ...f,
              size: mergedSize,
              photoCount: mergedPhotos.length,
              photos: mergedPhotos,
            } : f));
          }
          const totalSize = downloadedPhotos.reduce((sum, p) => sum + p.size, 0);
          return [...prev, {
            id: `album-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: ALL_PHOTOS_ALBUM_NAME,
            type: 'album',
            source: 'cloud',
            size: totalSize,
            photoCount: downloadedPhotos.length,
            photos: downloadedPhotos,
          }];
        });

        // If the album was already queued/uploading, merge new photos into uploads UI and upload directly
        if (albumAlreadyQueued && uploadAlbumRef.current) {
          setUploads((prev) => prev.map((u) => (u.id === existingAlbumEntry.id ? {
            ...u,
            photos: [...(u.photos || []), ...downloadedPhotos],
            photoCount: (u.photos || []).length + downloadedPhotos.length,
            totalPhotos: (u.photos || []).length + downloadedPhotos.length,
            fileSize: (u.fileSize || 0) + downloadedPhotos.reduce((sum, p) => sum + p.size, 0),
          } : u)));
          uploadAlbumRef.current({
            id: existingAlbumEntry.id,
            name: ALL_PHOTOS_ALBUM_NAME,
            photos: downloadedPhotos,
          });
        }
      }
    }

    setShowAddMore(false);
  };

  // ── handleCombinedFileSelect ───────────────────────────────────────────────
  const handleCombinedFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files);

    if (files.length === 0) return;

    const videoFiles = [];
    const imageFiles = [];
    files.forEach((file) => {
      const ext = `.${file.name.split('.').pop().toLowerCase()}`;
      if (VIDEO_EXTENSIONS.includes(ext)) videoFiles.push(file);
      else if (IMAGE_EXTENSIONS.includes(ext)) imageFiles.push(file);
    });

    if (videoFiles.length === 0 && imageFiles.length === 0) {
      setAppSnackbar({ isVisible: true, message: 'Please select valid video or image files', type: 'error' });
      return;
    }

    const currentTotalSize = selectedFiles.reduce((sum, f) => sum + f.size, 0);
    const newSize = [...videoFiles, ...imageFiles].reduce((sum, f) => sum + f.size, 0);
    if (currentTotalSize + newSize > MAX_TOTAL_SIZE_BYTES) {
      setAppSnackbar({ isVisible: true, message: 'Upload limit exceeded! You can upload up to 10GB at a time.', type: 'error' });
      const inputEl = event.target;
      if (inputEl) setTimeout(() => { inputEl.value = ''; }, 0);
      return;
    }

    // Owner plan gating (Free plan → 15-item account cap): reject the whole
    // selection up front so nothing lands in the list as "waiting" — the
    // contributor can just re-pick fewer files. Each video/photo is one item.
    if (ownerPlanLimited) {
      const wouldBeItems = countSelectedItems(selectedFiles) + videoFiles.length + imageFiles.length;
      if (wouldBeItems > ownerRemainingSlots) {
        setLimitError(limitMessage(ownerRemainingSlots));
        const inputEl = event.target;
        if (inputEl) setTimeout(() => { inputEl.value = ''; }, 0);
        return;
      }
    }
    setLimitError('');

    const newVideoFiles = videoFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
name: file.name,
size: file.size,
type: 'video',
source: 'local',
      previewUrl: URL.createObjectURL(file),
    }));

    const newPhotos = imageFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
      previewUrl: URL.createObjectURL(file),
    }));

    // Check if the album is already queued (2nd+ batch scenario)
    const existingAlbumEntry = newPhotos.length > 0
      ? selectedFiles.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME)
      : null;
    const albumAlreadyQueued = existingAlbumEntry && queuedFileIdsRef.current.has(existingAlbumEntry.id);

    setSelectedFiles((prev) => {
      let next = newVideoFiles.length > 0 ? [...prev, ...newVideoFiles] : [...prev];

      if (newPhotos.length > 0) {
        const existingAlbum = next.find((f) => f.type === 'album' && f.name === ALL_PHOTOS_ALBUM_NAME);
        if (existingAlbum) {
          // Merge into existing album
          const mergedPhotos = [...(existingAlbum.photos || []), ...newPhotos];
          const mergedSize = mergedPhotos.reduce((sum, p) => sum + p.size, 0);
          next = next.map((f) => (f.id === existingAlbum.id ? {
            ...f,
            size: mergedSize,
            photoCount: mergedPhotos.length,
            photos: mergedPhotos,
          } : f));
        } else {
          // Create new album
          const totalSize = newPhotos.reduce((sum, p) => sum + p.size, 0);
          next = [...next, {
            id: `album-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            name: ALL_PHOTOS_ALBUM_NAME,
            type: 'album',
            source: 'local',
            size: totalSize,
            photoCount: newPhotos.length,
            photos: newPhotos,
          }];
        }
      }

      return next;
    });

    // If the album was already queued/uploading, merge new photos into uploads UI and upload directly
    if (albumAlreadyQueued && newPhotos.length > 0 && uploadAlbumRef.current) {
      setUploads((prev) => prev.map((u) => (u.id === existingAlbumEntry.id ? {
        ...u,
        photos: [...(u.photos || []), ...newPhotos],
        photoCount: (u.photos || []).length + newPhotos.length,
        totalPhotos: (u.photos || []).length + newPhotos.length,
        fileSize: (u.fileSize || 0) + newPhotos.reduce((sum, p) => sum + p.size, 0),
      } : u)));
      uploadAlbumRef.current({
        id: existingAlbumEntry.id,
        name: ALL_PHOTOS_ALBUM_NAME,
        photos: newPhotos,
      });
    }

    setShowAddMore(false);

    // Generate video poster thumbnails in background
    if (newVideoFiles.length > 0) {
      (async () => {
        const posterMap = {};
        await Promise.all(newVideoFiles.map(async (f) => {
          const poster = await captureVideoFrame(f.file);
          if (poster) posterMap[f.id] = poster;
        }));
        if (Object.keys(posterMap).length > 0) {
          setSelectedFiles((prev) => prev.map((f) => (posterMap[f.id] ? { ...f, posterUrl: posterMap[f.id] } : f)));
          // Propagate poster to upload entries (upload may have auto-started before poster was ready)
          setUploads((prev) => prev.map((u) => (posterMap[u.id] && !u.posterUrl ? { ...u, posterUrl: posterMap[u.id] } : u)));
        }
      })();
    }

    const inputEl2 = event.target;
    if (inputEl2) setTimeout(() => { inputEl2.value = ''; }, 0);
  }, [selectedFiles, setAppSnackbar, ownerPlanLimited, ownerRemainingSlots, limitMessage]);

  // ── Upload controls (wire to uploadManagerSecure) ──────────────────────────
  const handlePauseUpload = useCallback((uploadId) => {
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (upload?.secureUploadId) {
      uploadManagerSecure.pauseUpload(upload.secureUploadId);
    }
    setUploads((prev) => prev.map((u) => (u.id === uploadId && u.status === UPLOAD_STATUS.UPLOADING
      ? { ...u, status: UPLOAD_STATUS.PAUSED, isPaused: true } : u)));
  }, []);

  const handleResumeUpload = useCallback((uploadId) => {
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (upload?.secureUploadId) {
      uploadManagerSecure.resumeUpload(upload.secureUploadId);
    }
    setUploads((prev) => prev.map((u) => (u.id === uploadId
      && (u.status === UPLOAD_STATUS.PAUSED || u.status === UPLOAD_STATUS.NETWORK_ERROR)
      ? { ...u, status: UPLOAD_STATUS.UPLOADING, isPaused: false } : u)));
  }, []);

  const handleCancelUpload = useCallback((uploadId) => {
    const upload = uploadsRef.current.find((u) => u.id === uploadId);
    if (upload?.secureUploadId) {
      uploadManagerSecure.cancelUpload(upload.secureUploadId);
    }
    setUploads((prev) => prev.map((u) => (u.id === uploadId ? { ...u, status: UPLOAD_STATUS.CANCELLED } : u)));
    setTimeout(() => {
      setUploads((prev) => prev.filter((u) => u.id !== uploadId));
      setSelectedFiles((prev) => prev.filter((f) => f.id !== uploadId));
    }, 1000);
    queuedFileIdsRef.current.delete(uploadId);
  }, []);

  // ── Upload a single video: thumbnails → video → submit ────────────────────
  const uploadVideo = useCallback(async (fileData) => {
    const { id, file, name } = fileData;
    const contName = contributorName.trim() || localStorage.getItem('contributorsName') || 'Contributor';

    // 1. Generate 6 thumbnails
setUploads((prev) => prev.map((u) => (
  u.id === id
    ? {
        ...u,
        status: UPLOAD_STATUS.UPLOADING,
        statusText: 'Uploading thumbnails...',
        isThumbnailUploading: true, // ✅ important
      }
    : u
)));

    const thumbData = await generateThumbnailsFromVideo(file);
    const thumbnailS3Keys = [];

    // 2. Upload each thumbnail to S3 via gift session
    for (let i = 0; i < thumbData.thumbnails.length; i += 1) {
      try {
        const thumbFile = base64ToFile(thumbData.thumbnails[i], `thumb_${i}.jpg`);
        // eslint-disable-next-line no-await-in-loop
        const s3Key = await uploadSingleFileGift(uuid, thumbFile, 'image/jpeg');
        thumbnailS3Keys.push(s3Key);
      } catch (err) {
        console.error(`[Contribute] Thumbnail ${i} upload failed:`, err);
      }
    }

    // 3. Upload video via uploadLargeFile with giftUuid. draft_id must be a
    // generated UUID (same convention as the main video upload flow) — the
    // submit API expects it in that form.
    const draftId = uuidv4();

    setUploads((prev) => prev.map((u) => (u.id === id
      ? { ...u, secureUploadId: draftId, statusText: 'Uploading video...', isThumbnailUploading: false } : u)));

    return new Promise((resolveUpload) => {
      uploadLargeFile({
        userId: uuid,
        file,
        draftId,
        thumbnails: [],
        suppressToast: true,
        giftUuid: uuid,
        onProgress: (progress, totalUploaded, totalBytes) => {
          const progressPercent = Math.round(progress * 0.9);
          setUploads((prev) => prev.map((u) => {
            if (u.id !== id) return u;
            const now = Date.now();
            const timeDiff = (now - (u.lastUpdateTime || now)) / 1000;
            const bytesDiff = totalUploaded - (u.lastBytesUploaded || 0);
            const speed = timeDiff > 0 && bytesDiff > 0 ? bytesDiff / timeDiff : (u.speed || 0);
            const remaining = totalBytes - totalUploaded;
            const timeRemaining = speed > 0 ? remaining / speed : 0;
            return {
              ...u,
              progress: Math.max(progressPercent, u.progress || 0),
              bytesUploaded: totalUploaded,
              speed,
              timeRemaining,
              lastUpdateTime: now,
              lastBytesUploaded: totalUploaded,
            };
          }));
        },
        onSuccess: null,
        onError: (error) => {
          console.error('[Contribute] Video upload error:', error);
          setUploads((prev) => prev.map((u) => (u.id === id
            ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
          resolveUpload({ success: false, error });
        },
      });

      // Poll for s3_complete. The giftUuid upload path in uploadManagerSecure
      // intentionally skips its onSuccess callback (it stops at s3_complete),
      // so we watch the task status here and register once it's done. Capped
      // at MAX_POLL_MS so a stuck upload can't leak this interval or hold its
      // concurrency slot forever.
      const pollStart = Date.now();
      const MAX_POLL_MS = 15 * 60 * 1000;
      const pollInterval = setInterval(() => {
        const status = uploadManagerSecure.getUploadStatus?.(draftId);
        if (status === 's3_complete' || status === 'completed') {
          clearInterval(pollInterval);

          // Get S3 key from manager
          const managerUpload = uploadManagerSecure.getUpload?.(draftId);
          const videoS3Key = managerUpload?.s3Key || '';

          // S3 reported complete but we have no key to register — treat as a
          // failure rather than submitting a contribution with an empty file_key.
          if (!videoS3Key) {
            console.error('[Contribute] S3 complete but no s3Key for:', name);
            setUploads((prev) => prev.map((u) => (u.id === id
              ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
            resolveUpload({ success: false, error: new Error('Missing S3 key') });
            return;
          }

          // 4. Submit contribution
          const videoOrientation = thumbData.videoWidth > thumbData.videoHeight ? '1' : '2';
          const actionPayload = {
            draft_id: draftId,
            // Contributions are submitted without a title — the owner titles the
            // item later after approval (mirrors the Flutter app).
            title: '',
            category_id: '',
            category_name: `${contName}'s videos`,
            file_name: name,
            file_key: videoS3Key,
            document_id: '',
            bucket_folder_path: videoS3Key,
            description: '',
            video_size: changeSize(file.size),
            group_ids: JSON.stringify([]),
            visibility: '1',
            subcategory_id: '',
            playlist_ids: '',
            videoHeight: String(thumbData.videoHeight || ''),
            videoWidth: String(thumbData.videoWidth || ''),
            videoOreintation: videoOrientation,
            s3_status: 'completed',
            thumbnail: thumbnailS3Keys[0] || '',
            thumbnails: thumbnailS3Keys,
          };

          submitContribution({
            uuid,
            temp_token: tempToken,
            email: contributorEmail.trim(),
            name: contName,
            type: 'video',
            payload: {
              method: 'POST',
              endpoint: '/V6/addVideo',
              action_id: `vid_${draftId}`,
              action_type: 'addVideo',
              payload: actionPayload,
            },
          }).then(() => {
            setUploads((prev) => prev.map((u) => (u.id === id
              ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 } : u)));
            resolveUpload({ success: true });
          }).catch((err) => {
            console.error('[Contribute] Submit error:', err);
            setUploads((prev) => prev.map((u) => (u.id === id
              ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
            resolveUpload({ success: false, error: err });
          });
        } else if (status === 'failed' || status === 'cancelled') {
          clearInterval(pollInterval);
          setUploads((prev) => prev.map((u) => (u.id === id
            ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
          resolveUpload({ success: false });
        } else if (Date.now() - pollStart > MAX_POLL_MS) {
          clearInterval(pollInterval);
          console.error('[Contribute] Upload timed out waiting for S3 completion:', name);
          setUploads((prev) => prev.map((u) => (u.id === id
            ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
          resolveUpload({ success: false, error: new Error('Upload timed out') });
        }
      }, 500);
    });
  }, [uuid, tempToken, contributorName, contributorEmail]);

  // ── Upload an album: each photo → submit ───────────────────────────────────
  const uploadAlbum = useCallback(async (fileData) => {
    const {
      id, name: albumName, photos: allPhotos,
    } = fileData;
    // Only upload photos with actual file data (skip mobile placeholders)
    const photos = (allPhotos || []).filter((p) => p.file);
    if (photos.length === 0) return { success: true }; // nothing to upload (e.g. mobile-only album)
    const contName = contributorName.trim() || localStorage.getItem('contributorsName') || 'Contributor';
    const totalSize = photos.reduce((sum, p) => sum + p.size, 0);
    let totalUploaded = 0;
    const photoS3Data = [];

    setUploads((prev) => prev.map((u) => (u.id === id
      ? { ...u, status: UPLOAD_STATUS.UPLOADING } : u)));

    // Upload each photo via gift session
    for (let i = 0; i < photos.length; i += 1) {
      const photo = photos[i];
      try {
        const uploadedSoFar = totalUploaded;

        // eslint-disable-next-line no-await-in-loop
        const session = await createGiftUploadSession(uuid, {
          file_name: photo.name,
          file_size: photo.file?.size || photo.size,
          content_type: photo.file?.type || 'image/jpeg',
        });

        const presignedUrl = session.presigned_urls?.[0]?.presigned_url;
        if (!presignedUrl) throw new Error('No presigned URL for photo');

        // eslint-disable-next-line no-await-in-loop
        const partResult = await uploadPartWithPresignedUrl(
          presignedUrl,
          photo.file,
          1,
          (loaded, total) => {
            const photoProgress = loaded / total;
            const overallUploaded = uploadedSoFar + (photo.size * photoProgress);
            const progressPercent = Math.round((overallUploaded / totalSize) * 95);
            setUploads((prev) => prev.map((u) => (u.id === id
              ? { ...u, progress: progressPercent, bytesUploaded: overallUploaded } : u)));
          },
        );

        // eslint-disable-next-line no-await-in-loop
        const completeResponse = await completeGiftUploadSession(session.id, uuid, [partResult]);
        const s3Key = completeResponse?.s3_key || completeResponse?.file_key
          || session.s3_key || session.file_key;

        photoS3Data.push({ path: s3Key, size: String(photo.size), name: photo.name });
        totalUploaded += photo.size;
      } catch (err) {
        console.error('[Contribute] Photo upload error:', photo.name, err);
      }
    }

    if (photoS3Data.length === 0) {
      setUploads((prev) => prev.map((u) => (u.id === id
        ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
      return { success: false };
    }

    // Submit album contribution
    try {
      await submitContribution({
        uuid,
        temp_token: tempToken,
        email: contributorEmail.trim(),
        name: contName,
        type: 'album',
        payload: {
          method: 'POST',
          endpoint: '/createAndAttachAlbum',
          action_id: `album_${id}`,
          action_type: 'createAndAttachAlbum',
          payload: {
            icon: photoS3Data[0]?.path || '',
            title: `${contName}'s Album`,
            photos: photoS3Data,
            group_id: '',
            visibility: '2',
            description: '',
          },
        },
      });

      setUploads((prev) => prev.map((u) => (u.id === id
        ? { ...u, status: UPLOAD_STATUS.COMPLETED, progress: 100 } : u)));
      return { success: true };
    } catch (err) {
      console.error('[Contribute] Album submit error:', err);
      setUploads((prev) => prev.map((u) => (u.id === id
        ? { ...u, status: UPLOAD_STATUS.FAILED } : u)));
      return { success: false, error: err };
    }
  }, [uuid, tempToken, contributorName, contributorEmail]);
  uploadAlbumRef.current = uploadAlbum;

  // ── Start upload: orchestrate queue ────────────────────────────────────────
  const handleStartUpload = useCallback(async () => {
    // Mobile-sourced entries (source: 'mobile') are progress placeholders only —
    // the actual upload runs on the phone and results arrive over SSE. They must
    // never enter the desktop upload pipeline: doing so would run uploadAlbum/
    // uploadVideo on photos/videos that have no local File, flip the card's
    // isMobileUpload flag off, and leave the "X/Y photos" progress stuck.
    const filesToUpload = selectedFiles.filter(
      (f) => !queuedFileIdsRef.current.has(f.id) && f.source !== 'mobile',
    );

    if (filesToUpload.length === 0) return;

    if (!uuid) {
      console.error('[Contribute] No UUID available - cannot upload');
      setAppSnackbar({ isVisible: true, message: 'Link validation failed. Cannot upload.', type: 'error' });
      return;
    }

    setIsUploading(true);

    // Initialize upload entries
    const newUploads = filesToUpload.map((f) => {
      fileDataRef.current.set(f.id, f);
      const isAlbum = f.type === 'album';
      const desktopPhotoCount = isAlbum ? (f.photos || []).filter((p) => !p.isMobile).length : 0;
      return {
        id: f.id,
        fileName: f.name,
        fileSize: f.size,
        type: f.type,
        status: UPLOAD_STATUS.PENDING,
        progress: 0,
        bytesUploaded: 0,
        speed: 0,
        timeRemaining: 0,
        isPaused: false,
        secureUploadId: null,
        photos: f.photos || null,
        previewUrl: f.previewUrl || null,
        posterUrl: f.posterUrl || null,
        photoCount: f.photoCount || 0,
        lastUpdateTime: null,
        lastBytesUploaded: 0,
        s3Key: null,
        isThumbnailUploading: false,
        ...(isAlbum ? { isPhotoAlbum: true, baseDesktopPhotos: desktopPhotoCount, totalPhotos: f.photoCount || 0 } : {}),
      };
    });
    // Merge with existing uploads — if an album upload entry already exists (from mobile SSE), update it instead of duplicating
    setUploads((prev) => {
      let updated = [...prev];
      const toAdd = [];
      newUploads.forEach((nu) => {
        if (nu.isPhotoAlbum) {
          const existingAlbum = updated.find((u) => u.isPhotoAlbum);
          if (existingAlbum) {
            // Merge into existing album upload entry
            const desktopPhotos = (nu.photos || []).filter((p) => !p.isMobile);
            const mobilePhotos = (existingAlbum.photos || []).filter((p) => p.isMobile);
            const mergedPhotos = [...desktopPhotos, ...mobilePhotos];
            const baseDesktop = desktopPhotos.length;
            const mobileCount = existingAlbum.mobilePhotoCount || 0;
            updated = updated.map((u) => (u.id === existingAlbum.id ? {
              ...u,
              id: nu.id,
              status: UPLOAD_STATUS.PENDING,
              fileSize: (u.fileSize || 0) + (nu.fileSize || 0),
              baseDesktopPhotos: baseDesktop,
              totalPhotos: baseDesktop + mobileCount,
              photoCount: baseDesktop + mobileCount,
              photos: mergedPhotos,
              isMobileUpload: false,
            } : u));
            return;
          }
        }
        toAdd.push(nu);
      });
      return [...toAdd, ...updated];
    });
    filesToUpload.forEach((f) => queuedFileIdsRef.current.add(f.id));

    // Separate videos and albums
    const videoFiles = filesToUpload.filter((f) => f.type === 'video');
    const albumFiles = filesToUpload.filter((f) => f.type === 'album');

    // Upload videos with concurrency
    const uploadVideoQueue = async () => {
      const queue = [...videoFiles];
      const active = new Set();
      const results = [];

      const processNext = () => new Promise((resolve) => {
        const checkQueue = () => {
          while (active.size < 3 && queue.length > 0) {
            const fileData = queue.shift();
            active.add(fileData.id);
            uploadVideo(fileData).then((result) => {
              results.push(result);
              active.delete(fileData.id);
              if (queue.length === 0 && active.size === 0) resolve();
              else checkQueue();
            });
          }
          if (queue.length === 0 && active.size === 0) resolve();
        };
        checkQueue();
      });

      if (videoFiles.length > 0) await processNext();
      return results;
    };

    // Upload albums sequentially
    const uploadAlbumQueue = async () => {
      const results = [];
      for (let i = 0; i < albumFiles.length; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        const result = await uploadAlbum(albumFiles[i]);
        results.push(result);
      }
      return results;
    };

    await Promise.allSettled([uploadVideoQueue(), uploadAlbumQueue()]);

    setTimeout(() => {
      const hasActive = uploadsRef.current.some(
        (u) => u.status === UPLOAD_STATUS.UPLOADING || u.status === UPLOAD_STATUS.PAUSED || u.status === UPLOAD_STATUS.PENDING,
      );
      if (!hasActive) setIsUploading(false);
    }, 200);
  }, [selectedFiles, uuid, uploadVideo, uploadAlbum, setAppSnackbar]);

  // ── Auto-start upload when new files are selected (same as gift flow) ──────
  useEffect(() => {
    if (selectedFiles.length === 0 || !uuid) return;
    // Skip mobile-sourced entries — they upload on the phone and only mirror
    // progress here via SSE, so they must not trigger a desktop upload.
    const newFiles = selectedFiles.filter(
      (f) => !queuedFileIdsRef.current.has(f.id) && f.source !== 'mobile',
    );
    if (newFiles.length === 0) return;

    handleStartUpload();
  }, [selectedFiles, uuid, handleStartUpload]);

  // ── Existing (already-uploaded) contributions → card shape ─────────────────
  // Normalize the contributor's prior uploads (fetched from the backend) into
  // the same card shape the tab renderers use, so existing content and
  // in-progress uploads render together in one tabbed view. These always show
  // as COMPLETED; the delete/Published affordances are preserved via
  // `isExisting`/`contributionId`.
  const existingCards = useMemo(() => (myUploads || []).map((item) => {
    const isAlbum = item.type === 'album';
    const rawPhotos = Array.isArray(item.photos) ? item.photos : [];
    const photos = rawPhotos.map((p, i) => ({
      id: p.id || `${item.id}-p${i}`,
      name: p.name || '',
      previewUrl: p.signed_url || p.thumbnail_signed || p.previewUrl || p.url || null,
      thumbnailUrl: p.thumbnail_signed || p.thumbnailUrl || null,
    }));
    const published = !!item.is_published
      || (!!item.connected_source && item.connected_source !== '');
    return {
      id: `existing-${item.id}`,
      contributionId: item.id,
      isExisting: true,
      isPublished: published,
      fileName: resolveContributionTitle(item.title) || (isAlbum ? 'Photo Album' : ''),
      type: isAlbum ? 'album' : 'video',
      isPhotoAlbum: isAlbum,
      status: UPLOAD_STATUS.COMPLETED,
      progress: 100,
      // For albums, thumbnail_signed is the cover — used when the backend
      // didn't return the individual photos to build a grid.
      thumbnail: item.thumbnail_signed || null,
      posterUrl: item.thumbnail_signed || null,
      photos: isAlbum ? photos : null,
      photoCount: isAlbum ? (item.photo_count || photos.length || 0) : 0,
      createdAt: item.created_at,
      // Used to group cards into per-contributor folders. Empty for the
      // caller's own items (backend leaves contributor_email blank for them),
      // which the folder grouping buckets into the current contributor's folder.
      contributorEmail: (item.contributor_email || '').trim(),
    };
  }), [myUploads]);

  // Current session's files as cards: once an upload has started `uploads` is
  // the source of truth; before that, preview the not-yet-started selection.
  const sessionCards = useMemo(() => {
    if (uploads.length > 0) return uploads;
    return selectedFiles.map((file) => ({
      id: file.id,
      fileName: file.name,
      fileSize: file.size,
      type: file.type,
      status: UPLOAD_STATUS.PENDING,
      progress: 0,
      bytesUploaded: 0,
      speed: 0,
      timeRemaining: 0,
      photos: file.photos || null,
      previewUrl: file.previewUrl || null,
      posterUrl: file.posterUrl || null,
      photoCount: file.photoCount || 0,
    }));
  }, [uploads, selectedFiles]);

  // Existing content first, then this session's in-progress/selected items.
  // ── Per-contributor folders (mobile parity) ────────────────────────────────
  // All cards (previously-uploaded backend items + the in-progress session)
  // are grouped into one folder per distinct contributor email. Items with an
  // empty contributor_email (the caller's own uploads + the session) fall into
  // the current contributor's folder. Within each folder, every photo-album is
  // collapsed into ONE album card. Today the backend only returns the caller's
  // own items, so this yields a single folder; it widens automatically if the
  // endpoint starts returning other contributors' items.
  const folders = useMemo(() => {
    const currentEmail = contributorEmail.trim();
    const currentKey = currentEmail.toLowerCase();
    const all = [...existingCards, ...sessionCards];
    const groups = new Map(); // key → { key, cards[] }
    all.forEach((card) => {
      const rawEmail = (card.contributorEmail || '').trim();
      const key = (rawEmail || currentKey).toLowerCase();
      if (!groups.has(key)) groups.set(key, { key, cards: [] });
      groups.get(key).cards.push(card);
    });

    const list = Array.from(groups.values()).map((g) => {
      const merged = mergeAlbumCards(g.cards);
      const videos = merged.filter((c) => !isAlbumCard(c));
      const album = merged.find(isAlbumCard) || null;
      const isCurrent = g.key === currentKey;
      // The current folder's display email is what the contributor typed
      // (their own items carry an empty contributor_email); other folders use
      // the email that grouped them.
      const email = isCurrent
        ? currentEmail
        : (g.cards.find((c) => (c.contributorEmail || '').trim())?.contributorEmail || '').trim();
      const isUploading = merged.some(
        (c) => c.status === UPLOAD_STATUS.UPLOADING || c.status === UPLOAD_STATUS.PENDING,
      );
      const coverCard = merged.find((c) => c.thumbnail || c.posterUrl || c.previewUrl);
      return {
        key: g.key,
        email,
        displayName: folderNameFromEmail(email),
        cards: merged,
        videos,
        album,
        isCurrent,
        isUploading,
        cover: coverCard
          ? (coverCard.thumbnail || coverCard.posterUrl || coverCard.previewUrl)
          : null,
      };
    });
    // Current contributor's folder is always listed first.
    list.sort((a, b) => {
      if (a.isCurrent === b.isCurrent) return 0;
      return a.isCurrent ? -1 : 1;
    });
    return list;
  }, [existingCards, sessionCards, contributorEmail]);

  // Flattened views for the top-level Videos / Photos tabs (everyone's items).
  const allVideos = useMemo(() => folders.flatMap((f) => f.videos), [folders]);
  const allAlbums = useMemo(() => folders.map((f) => f.album).filter(Boolean), [folders]);

  // The folder the contributor has drilled into (Level 2), or null at Level 1.
  const openFolder = useMemo(
    () => folders.find((f) => f.key === openFolderKey) || null,
    [folders, openFolderKey],
  );

  // If the open folder disappears (e.g. after a post-upload reconcile), drop
  // back to the folder grid instead of showing an empty screen.
  useEffect(() => {
    if (openFolderKey && !openFolder) setOpenFolderKey(null);
  }, [openFolderKey, openFolder]);

  // Cards shown inside the open folder, filtered by its All / Videos / Photos
  // sub-tab.
  const folderFilteredCards = useMemo(() => {
    if (!openFolder) return [];
    if (contentFilter === 'videos') return openFolder.videos;
    if (contentFilter === 'photos') return openFolder.album ? [openFolder.album] : [];
    return openFolder.cards;
  }, [openFolder, contentFilter]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  // The tabbed view is the primary surface whenever there is any content —
  // existing uploads OR a current selection. The big options grid is reserved
  // for the empty state and the explicit "add more" (mobile/QR) flow.
  const hasSessionFiles = selectedFiles.length > 0;
  const hasExisting = existingCards.length > 0;
  const hasAnyContent = hasSessionFiles || hasExisting;
  const showOptionsGrid = !showContributorPopup
    && (showAddMore || (!hasAnyContent && !myUploadsLoading));
  const showFilesContainer = !showContributorPopup && hasAnyContent && !showAddMore;

  // Reconcile a finished batch with the backend: re-fetch the contributor's
  // uploads (and the owner plan), THEN clear the session's in-progress cards.
  // The backend list can lag (read-after-write) right after the last
  // contribution is submitted, so we POLL `listMyContributions` until it
  // reflects the new items (baseline + this batch's count) before clearing —
  // otherwise the just-uploaded cards would vanish until a manual refresh.
  const reconcilingRef = useRef(false);
  const reconcileAfterUpload = useCallback(async () => {
    if (reconcilingRef.current) return;
    reconcilingRef.current = true;
    const email = contributorEmail.trim();
    const baseline = myUploadsRef.current.length;
    const expected = uploadsRef.current.length; // each upload entry → 1 contribution
    // Snapshot the just-uploaded album's local photos BEFORE the session clears.
    // `listMyContributions` returns only ONE cover `thumbnail_signed` per album
    // record (no per-photo array), so we re-attach these local previews to the
    // freshly-created backend album record below — otherwise a 3-photo album
    // would collapse to a single cover tile. Everything else (video cards,
    // delete affordances, plan limits) reconciles from the backend as before.
    const sessionAlbum = uploadsRef.current.find((u) => u.isPhotoAlbum || u.type === 'album');
    const sessionAlbumPhotos = sessionAlbum && Array.isArray(sessionAlbum.photos)
      ? sessionAlbum.photos.filter((p) => p.previewUrl || p.thumbnailUrl)
      : [];
    let finalList = null;
    try {
      if (uuid && tempToken && email) {
        for (let attempt = 0; attempt < 6; attempt += 1) {
          // eslint-disable-next-line no-await-in-loop
          const res = await listMyContributions({ temp_token: tempToken, email });
          finalList = res?.data?.success ? (res.data.contributions || []) : [];
          if (finalList.length >= baseline + expected) break;
          // eslint-disable-next-line no-await-in-loop
          await new Promise((resolve) => setTimeout(resolve, 700));
        }
      }
      await fetchOwnerPlan();
    } catch (err) {
      finalList = null;
    } finally {
      // Re-attach the local album photos to the newest backend album record so
      // the grid keeps every uploaded photo (backend returns only a cover).
      if (finalList && sessionAlbumPhotos.length > 0) {
        const albumRecords = finalList.filter((it) => it.type === 'album');
        if (albumRecords.length > 0) {
          const newest = albumRecords.reduce((a, b) => (
            new Date(b.created_at || 0).getTime() >= new Date(a.created_at || 0).getTime() ? b : a
          ));
          const attachedPhotos = sessionAlbumPhotos.map((p, i) => ({
            id: p.id || `${newest.id}-p${i}`,
            name: p.name || '',
            previewUrl: p.previewUrl || p.thumbnailUrl || null,
            thumbnail_signed: p.previewUrl || p.thumbnailUrl || null,
          }));
          finalList = finalList.map((it) => (it.id === newest.id
            ? { ...it, photos: attachedPhotos, photo_count: attachedPhotos.length }
            : it));
        }
      }
      // Commit the fresh list and clear the session together (no await between)
      // so the just-uploaded cards never disappear and duplicates don't linger.
      if (finalList) setMyUploads(finalList);
      else fetchMyUploads();
      setLimitError('');
      setUploads([]);
      setSelectedFiles([]);
      queuedFileIdsRef.current = new Set();
      trackedMobilePhotoUidsRef.current = new Set();
      uploadAlbumRef.current = null;
      setIsUploading(false);
      reconcilingRef.current = false;
    }
  }, [uuid, tempToken, contributorEmail, fetchOwnerPlan, fetchMyUploads]);

  // No Finish button — when a batch finishes uploading (every item complete),
  // silently refresh from the backend so the saved cards + plan limits update.
  // The ref keeps it to one run per batch; it resets once the session clears.
  const autoFinishRef = useRef(false);
  useEffect(() => {
    if (uploads.length === 0) {
      autoFinishRef.current = false;
      return;
    }
    const allComplete = uploads.every((u) => u.status === UPLOAD_STATUS.COMPLETED);
    if (allComplete && !autoFinishRef.current) {
      autoFinishRef.current = true;
      setShowAddMore(false);
      setContentFilter('all');
      reconcileAfterUpload();
      setTimeout(() => window.scrollTo(0, 0), 100);
    }
  }, [uploads, reconcileAfterUpload]);

  const getAlbumGridClass = (count) => {
    if (count <= 4) return `${styles.albumFolderGrid} ${styles.albumFolderGrid2x2}`;
    if (count <= 6) return `${styles.albumFolderGrid} ${styles.albumFolderGrid3x2}`;
    return styles.albumFolderGrid;
  };

  // ── Render: filtered card for Videos/Photos tab (matches QuickUserUploadPage) ─
  // In-progress upload status block shown inside a content card's info area —
  // mirrors gift-content's renderUploadCardStatus (name + %/status + pause /
  // resume / cancel for videos, progress bar, size). Uses /contribute's own
  // handlers (id-based) so upload control functionality is unchanged.
  const renderCardUploadStatus = (upload) => {
    // Videos NEVER show a title — including while uploading ("Uploading
    // thumbnails…" / the progress bar). Albums keep their title in every state
    // (mobile parity: hideName: true for in-progress video cards).
    const isAlbum = upload.type === 'album' || upload.isPhotoAlbum;
    const name = isAlbum ? (upload.fileName || 'Photo Album') : '';
    // No pause/cancel while thumbnails upload (matches the original card) —
    // avoids the controls getting cramped against the "Uploading thumbnails…"
    // status text.
    const showControls = upload.type === 'video' && !upload.isMobileUpload && !upload.isPhotoAlbum
      && !upload.isThumbnailUploading
      && (upload.status === UPLOAD_STATUS.UPLOADING
        || upload.status === UPLOAD_STATUS.PAUSED
        || upload.status === UPLOAD_STATUS.NETWORK_ERROR);
    return (
      <>
        <div className={styles.uploadProgressHeader}>
          {name && (
            <div className={styles.filteredCardTitleRow}>
              <span className={styles.uploadFileName}>
                {name.length > 24 ? `${name.substring(0, 24)}...` : name}
              </span>
            </div>
          )}
          <div className={styles.uploadStatusActions}>
            <span className={styles.uploadStatus}>
              {upload.status === UPLOAD_STATUS.UPLOADING && upload.isMobileUpload && (
                <span className={styles.mobileUploadStatus}>
                  Uploading from Mobile...
                  <span className={styles.mobileUploadSpinner} />
                </span>
              )}
              {upload.status === UPLOAD_STATUS.UPLOADING && upload.isPhotoAlbum && !upload.isMobileUpload && (
                <span>{`${upload.uploadedPhotos || 0}/${upload.totalPhotos || '?'} photos`}</span>
              )}
              {upload.status === UPLOAD_STATUS.UPLOADING && !upload.isMobileUpload && !upload.isPhotoAlbum && (
                upload.isThumbnailUploading ? (
                  <span className={styles.mobileUploadStatus}>
                    Uploading thumbnails...
                    <span className={styles.mobileUploadSpinner} />
                  </span>
                ) : `${upload.progress || 0}%`
              )}
              {upload.status === UPLOAD_STATUS.PAUSED && 'Paused'}
              {upload.status === UPLOAD_STATUS.NETWORK_ERROR && 'Network Error'}
              {upload.status === UPLOAD_STATUS.PENDING && 'Waiting...'}
              {upload.status === UPLOAD_STATUS.CANCELLING && 'Cancelling...'}
              {upload.status === UPLOAD_STATUS.CANCELLED && 'Cancelled'}
              {upload.status === UPLOAD_STATUS.FAILED && (
                <button
                  type="button"
                  onClick={() => handleCancelUpload(upload.id)}
                  style={{ cursor: 'pointer', background: 'none', border: 'none', color: 'inherit', font: 'inherit', padding: 0 }}
                  title="Remove"
                >
                  ✕
                </button>
              )}
            </span>
            {showControls && (
              <div className={styles.uploadControls}>
                {upload.status === UPLOAD_STATUS.UPLOADING && (
                  <button type="button" className={styles.uploadControlButton} onClick={() => handlePauseUpload(upload.id)} title="Pause upload">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                    </svg>
                  </button>
                )}
                {(upload.status === UPLOAD_STATUS.PAUSED || upload.status === UPLOAD_STATUS.NETWORK_ERROR) && (
                  <button type="button" className={styles.uploadControlButton} onClick={() => handleResumeUpload(upload.id)} title="Resume upload">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </button>
                )}
                <button type="button" className={styles.uploadControlButtonCancel} onClick={() => handleCancelUpload(upload.id)} title="Cancel upload">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
        <div className={styles.uploadProgressBarContainer}>
          <div
            className={`${styles.uploadProgressBar}${upload.status === UPLOAD_STATUS.PAUSED ? ` ${styles.uploadProgressPaused}` : ''}${upload.status === UPLOAD_STATUS.FAILED ? ` ${styles.uploadProgressFailed}` : ''}${(upload.isMobileUpload || upload.isThumbnailUploading) && upload.status === UPLOAD_STATUS.UPLOADING ? ` ${styles.uploadProgressIndeterminate}` : ''}`}
            style={{ width: (upload.isMobileUpload || upload.isThumbnailUploading) ? '100%' : `${upload.progress || 0}%` }}
          />
        </div>
        <div className={styles.uploadProgressDetails}>
          {upload.status === UPLOAD_STATUS.UPLOADING && !upload.isMobileUpload && !upload.isPhotoAlbum && (
            <span>{formatFileSize(upload.fileSize)}</span>
          )}
          {upload.isPhotoAlbum && !upload.isMobileUpload && upload.status !== UPLOAD_STATUS.FAILED && (
            <span>{`${upload.uploadedPhotos || 0}/${upload.totalPhotos || 0} photos`}</span>
          )}
          {upload.isMobileUpload && upload.status !== UPLOAD_STATUS.FAILED && <span>Uploading from mobile</span>}
          {upload.status === UPLOAD_STATUS.FAILED && (
            <span className={styles.uploadFailedText}>Failed</span>
          )}
        </div>
      </>
    );
  };

  // Card for the Videos / Photo Album tabs — same UI as gift-content
  // (contentCard: thumbnail + title row + meta, album grid, shimmer/progress
  // while uploading). Functionality (preview, delete-confirm, upload controls)
  // is /contribute's existing behavior, just rendered on this card.
  const renderFilteredCard = (upload) => {
    const isAlbum = upload.type === 'album' || upload.isPhotoAlbum;
    const albumPhotos = upload.photos || [];
    const isUploadingStatus = upload.status === UPLOAD_STATUS.UPLOADING || upload.status === UPLOAD_STATUS.PENDING;
    const { isExisting } = upload;
    // A session card in any non-completed state (uploading, paused, network
    // error, failed, cancelling/cancelled) shows the status block + controls —
    // preserving the behaviour the old All-tab card had.
    const isInProgress = !isExisting && upload.status !== UPLOAD_STATUS.COMPLETED;
    // Lock pointer events only while actively uploading / paused (prevents
    // stray thumbnail clicks). Failed / cancelled cards stay interactive so
    // their remove (✕) button works. Controls re-enable via .uploadControls.
    const lockCard = isInProgress
      && upload.status !== UPLOAD_STATUS.FAILED
      && upload.status !== UPLOAD_STATUS.CANCELLED;
    // Videos NEVER show a title (mobile parity): the completed video card shows
    // only the date (+ contributor email) and the video icon. Albums ALWAYS show
    // a title, falling back to "Photo Album" when the backend title is empty or a
    // date-like placeholder.
    const displayName = isAlbum ? (upload.fileName || 'Photo Album') : '';
    const confirming = isExisting && confirmDeleteId === upload.contributionId;
    const busyDeleting = deletingId === upload.contributionId;
    // Upload date for previously-uploaded items (shown in the meta line, same
    // as the pre-merge card).
    let dateLabel = '';
    if (upload.createdAt) {
      try {
        dateLabel = new Date(upload.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (e) { dateLabel = ''; }
    }
    // Contributor email shown under the date — mobile parity. Only rendered
    // when non-empty; the caller's own items leave it blank so they show no
    // email, while other contributors' cards surface who added them.
    const contributorLine = (upload.contributorEmail || '').trim();

    return (
      <div key={upload.id} className={`${styles.contentCard}${lockCard ? ` ${styles.uploadingCard}` : ''}`}>
        <div className={styles.contentCardThumbnail}>
          {/* Thumbnail: album grid, album cover, video poster, or placeholder */}
          {isAlbum && albumPhotos.length > 0 ? (
            <div className={getAlbumGridClass(albumPhotos.length)}>
              {albumPhotos.slice(0, albumPhotos.length > 9 ? 8 : 9).map((photo, idx) => (
                <div key={photo.id || idx}>
                  {(photo.previewUrl || photo.thumbnailUrl) ? (
                    <img src={photo.previewUrl || photo.thumbnailUrl} alt={photo.name || `Photo ${idx + 1}`} />
                  ) : null}
                </div>
              ))}
              {albumPhotos.length > 9 && (
                <div className={styles.albumFolderGridCount}>{`+${albumPhotos.length - 8}`}</div>
              )}
            </div>
          ) : isAlbum && upload.thumbnail ? (
            <img src={upload.thumbnail} alt={displayName} />
          ) : !isAlbum && (upload.posterUrl || upload.thumbnail) ? (
            <img src={upload.posterUrl || upload.thumbnail} alt={displayName} />
          ) : !isAlbum && upload.isMobileUpload && upload.s3Url && upload.status === UPLOAD_STATUS.COMPLETED ? (
            <video src={upload.s3Url} muted playsInline preload="metadata" />
          ) : !isAlbum && upload.previewUrl ? (
            <video src={upload.previewUrl} muted playsInline preload="metadata" />
          ) : (
            <div className={styles.uploadingCardPlaceholder}>
              {isAlbum ? (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
              ) : (
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#555"><path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" /></svg>
              )}
            </div>
          )}

          {/* Shimmer while uploading */}
          {isUploadingStatus && <div className={styles.uploadingCardShimmer} />}

          {/* Completed album → preview eye */}
          {!isUploadingStatus && isAlbum && albumPhotos.length > 0 && (
            <button
              type="button"
              className={styles.previewEyeButton}
              title="Preview photos"
              onClick={() => setAlbumModal({ photos: albumPhotos })}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="3" stroke="white" strokeWidth="2" />
              </svg>
            </button>
          )}

          {/* Existing, not-yet-published → delete (trash) with confirm overlay */}
          {isExisting && !upload.isPublished && !confirming && (
            <button
              type="button"
              className={styles.deleteCardButton}
              title="Delete"
              onClick={() => setConfirmDeleteId(upload.contributionId)}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <polyline points="3 6 5 6 21 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M19 6L18.1 20.1C18 21.2 17.1 22 16 22H8C6.9 22 6 21.2 5.9 20.1L5 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M10 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M14 11V17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M9 6V4C9 3.4 9.4 3 10 3H14C14.6 3 15 3.4 15 4V6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          )}
          {confirming && (
            <div className={styles.cardDeleteConfirm}>
              <button
                type="button"
                className={styles.cardDeleteConfirmBtn}
                onClick={() => (upload.mergedAlbumIds
                  ? handleDeleteMergedAlbum(upload.mergedAlbumIds)
                  : handleDeleteMyUpload(upload.contributionId))}
                disabled={busyDeleting}
              >
                {busyDeleting && <span className={styles.deleteSpinner} aria-hidden="true" />}
                {busyDeleting ? 'Deleting…' : 'Delete'}
              </button>
              <button
                type="button"
                className={styles.cardDeleteConfirmCancel}
                onClick={() => setConfirmDeleteId(null)}
                disabled={busyDeleting}
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        <div className={styles.contentCardInfo}>
          {isInProgress ? (
            renderCardUploadStatus(upload)
          ) : (
            <>
              <div className={styles.contentCardTitleRow}>
                {displayName ? (
                  <h4 className={styles.contentCardTitle}>
                    {displayName.length > 18 ? `${displayName.substring(0, 18)}...` : displayName}
                  </h4>
                ) : (
                  <h4 className={styles.contentCardTitle} aria-hidden="true" />
                )}
                <span className={styles.contentTypeIcon}>
                  {isAlbum ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                      <circle cx="8.5" cy="8.5" r="1.5" />
                      <polyline points="21 15 16 10 5 21" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polygon points="23 7 16 12 23 17 23 7" />
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
                    </svg>
                  )}
                </span>
              </div>
              <div className={styles.contentCardMeta}>
                <span style={{
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0,
                }}
                >
                  {dateLabel || 'Uploaded'}
                </span>
                {isExisting && upload.isPublished && (
                  <span className={styles.cardPublishedBadge}>Published</span>
                )}
              </div>
              {contributorLine && (
                <div className={styles.contentCardMeta}>
                  <span className={styles.cardContributorEmail} title={contributorLine}>
                    {contributorLine}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  // ── Folder card (Level 1 "Folders" tab) ────────────────────────────────────
  // One card per contributor. Shows a cover thumbnail + the contributor's
  // display name (no count — mobile parity). The current contributor's own
  // folder is locked (non-tappable) while their upload is in progress.
  const renderFolderCard = (folder) => {
    const locked = folder.isCurrent && folder.isUploading;
    const open = () => { if (!locked) { setContentFilter('all'); setOpenFolderKey(folder.key); } };
    return (
      <div
        key={folder.key}
        className={`${styles.contentCard} ${styles.folderCard}${locked ? ` ${styles.folderCardDisabled}` : ''}`}
        role="button"
        tabIndex={locked ? -1 : 0}
        aria-disabled={locked}
        onClick={open}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      >
        {locked && <span className={styles.folderCardUploadingBadge}>Uploading...</span>}
        <div className={styles.contentCardThumbnail}>
          {folder.cover ? (
            <img src={folder.cover} alt={folder.displayName} />
          ) : (
            <div className={styles.folderCardPlaceholder}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
            </div>
          )}
          <span className={styles.folderCardBadge}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </span>
        </div>
        <div className={styles.contentCardInfo}>
          <div className={styles.contentCardTitleRow}>
            <h4 className={styles.contentCardTitle} title={folder.displayName}>
              {folder.isCurrent ? `${folder.displayName} (You)` : folder.displayName}
            </h4>
          </div>
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════════
  // JSX
  // ══════════════════════════════════════════════════════════════════════════════

  // Show error if link validation failed
  if (linkError) {
    return (
      <div className={styles.page}>
        <div className={styles.raysLayer} aria-hidden="true">
          <LightRays
            raysOrigin="top-center"
            raysColor="#ffffff"
            raysSpeed={0.9}
            lightSpread={0.85}
            rayLength={1.25}
            fadeDistance={1.1}
            saturation={1}
            followMouse
            mouseInfluence={0.08}
            noiseAmount={0.08}
            distortion={0.04}
          />
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorIconWrap}>
            <svg className={styles.errorIcon} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="#ff7a7a" />
              <circle cx="12" cy="12" r="11" stroke="#ff7a7a" strokeWidth="2" fill="none" />
            </svg>
          </div>
          <h2 className={styles.errorTitle}>{linkError}</h2>
          <p className={styles.errorText}>Please check the link and try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Backdrop: near-black with volumetric light rays fanning down from the
          top, same layer/props as /gift and /newSign-in. */}
      <div className={styles.raysLayer} aria-hidden="true">
        <LightRays
          raysOrigin="top-center"
          raysColor="#ffffff"
          raysSpeed={0.9}
          lightSpread={0.85}
          rayLength={1.25}
          fadeDistance={1.1}
          saturation={1}
          followMouse
          mouseInfluence={0.08}
          noiseAmount={0.08}
          distortion={0.04}
        />
      </div>
      {/* ── Contributor Name Popup ───────────────────────────────────────────── */}
      {/* ── "Open in app" arrival popup (mobile only, before name/email) ──── */}
      {showAppPopup && (
        <div className={styles.contributorOverlay}>
          <div className={styles.contributorCard}>
            <div className={styles.contributorIconWrap}>
              <div className={styles.contributorIconRing} />
              <svg className={styles.contributorIconSvg} viewBox="0 0 24 24" fill="#f5b53f" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
              </svg>
            </div>
            <h2 className={styles.contributorTitle}>Attention</h2>
            <p className={styles.contributorDescription}>{OPEN_APP_POPUP_TEXT}</p>
            <button
              type="button"
              className={styles.contributorContinueBtn}
              onClick={handleOpenApp}
            >
              GO TO APPLICATION
            </button>
            <button
              type="button"
              onClick={() => setShowAppPopup(false)}
              style={{
                marginTop: 12,
                background: 'none',
                border: 'none',
                color: '#5aa5ef',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              CONTINUE HERE
            </button>
          </div>
        </div>
      )}
      {showContributorPopup && !showAppPopup && (
        <div className={styles.contributorOverlay}>
          <div className={styles.contributorCard}>
            <div className={styles.contributorIconWrap}>
              <div className={styles.contributorIconRing} />
              <svg className={styles.contributorIconSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5z" fill="#5aa5ef" />
              </svg>
            </div>
            <h2 className={styles.contributorTitle}>
              {ownerFirstName ? `Welcome! Add to ${ownerFirstName}'s Projector` : 'Welcome, Contributor!'}
            </h2>
            <p className={styles.contributorDescription}>
              Enter your name and email or phone number so the recipient knows who added media.
            </p>
            {ownerEmail && (
              <p className={styles.contributorDescription} style={{ marginTop: '-16px', fontSize: '0.8rem' }}>
                {`Adding to: ${ownerEmail}`}
              </p>
            )}
            <div className={styles.contributorField}>
              <label className={styles.contributorLabel}>Your Name *</label>
              <input
                type="text"
                className={styles.contributorInput}
                value={contributorName}
                onChange={(e) => setContributorName(capitalizeWords(e.target.value))}
                onBlur={() => setContNameTouched(true)}
                placeholder="Enter your full name"
                onKeyDown={handleContributorKeyDown}
              />
              {contNameTouched && contNameValidReady && contributorName && contNameNoSpaces.length < 4 && (
                <p className={styles.contributorError}>Please enter a name with at least 4 characters</p>
              )}
              {contNameTouched && contNameValidReady && contributorName && contNameNoSpaces.length >= 4 && !contHasSpace && (
                <p className={styles.contributorError}>Last name is required</p>
              )}
            </div>
            <div className={styles.contributorField}>
              <label className={styles.contributorLabel}>Your Email or Phone *</label>
              <input
                type="text"
                className={styles.contributorInput}
                value={contributorEmail}
                onChange={(e) => setContributorEmail(e.target.value)}
                onBlur={() => setContEmailTouched(true)}
                placeholder="Enter your email or phone number"
                onKeyDown={handleContributorKeyDown}
              />
              {contEmailTouched && contEmailValidReady && !contEmailValid && (
                <p className={styles.contributorError}>Please enter a valid email address or phone number</p>
              )}
            </div>
            <button
              type="button"
              className={styles.contributorContinueBtn}
              disabled={!contFullNameValid || !contEmailValid}
              onClick={handleContributorContinue}
            >
              Continue
            </button>
          </div>
        </div>
      )}
      {/* ── Hidden file input ──────────────────────────────────────────────── */}
      <input
        type="file"
        ref={filesInputRef}
        style={{ display: 'none' }}
        multiple
        accept={FILE_ACCEPT}
        onChange={handleCombinedFileSelect}
      />
      {/* ── Pinned gallery-slowness warning (mobile only, same as mobile-upload) ── */}
      {isMobileDevice && !showAppPopup && !showContributorPopup && (
        <div className={styles.galleryWarningBanner}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="#f5b53f" aria-hidden="true">
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </svg>
          <span>{GALLERY_WARNING_TEXT}</span>
        </div>
      )}
      {/* ── Upload Options Grid ─────────────────────────────────────────────── */}
      {showOptionsGrid && (
        <div className={styles.contentContainer}>
          <h2 className={styles.stepSubHeading}>
            {ownerFirstName ? `Add Media To ${ownerFirstName}'s Projector` : 'Add Media To Their Projector'}
          </h2>
          {ownerEmail && (
            <p className={styles.stepSubText}>{`You are adding media to ${ownerEmail}'s account`}</p>
          )}
          <p className={styles.stepSubText}>You can choose one or more</p>

          {limitError && (
            <p style={{
              color: 'var(--lp-error)', fontSize: '1rem', fontWeight: 600, margin: '4px 0 12px', textAlign: 'center',
            }}
            >
              {limitError}
            </p>
          )}

          {/* Sources vs. QR are swapped, not flipped. The old rotateY(180deg)
              card relied on backface-visibility to hide the source grid, which
              the tiles' backdrop-filter defeats in Chrome: the grid stayed on
              screen mirrored, on top of the QR panel. Only one face is
              mounted now, so there is nothing left to leak through. */}
          <div className={styles.flipCardContainer}>
            {!showQrCode && (
            <div className={styles.flipCardFront}>
              <div className={styles.uploadOptionsGrid}>
                {/* Row 1 (primary, tall) — mirrors gift-content's upload grid:
                    local files + mobile hand-off get the dominant row. */}
                <div className={styles.uploadOptionsRow}>
                  <div aria-hidden="true" className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay1}`} onClick={() => filesInputRef.current?.click()}>
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 18V12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9 15L12 12L15 15" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <span className={`${styles.uploadOptionText} ${styles.hideOnMobile}`}>Choose Local Files</span>
                    <span className={`${styles.uploadOptionText} ${styles.showOnMobileOnly}`}>Choose From Gallery</span>
                  </div>
                  <div aria-hidden="true" className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay2} ${styles.hideOnMobile}`} onClick={() => setShowQrCode(true)}>
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="5" y="1" width="14" height="22" rx="3" stroke="white" strokeWidth="1.5" />
                        <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                        <circle cx="12" cy="19" r="1" fill="white" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Upload From Mobile</span>
                  </div>
                </div>
                {/* Row 2 (compact, cloud sources) */}
                <div className={`${styles.uploadOptionsRow} ${styles.uploadOptionsRowCompact}`}>
                  <div aria-hidden="true" className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay3} ${styles.hideOnMobile}`} onClick={() => handleCloudConnect('googlephotos')}>
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
                        <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
                        <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
                        <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
                        <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Google Photos</span>
                  </div>
                  <div aria-hidden="true" className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay4} ${styles.hideOnMobile}`} onClick={() => setShowQrCode(true)}>
                    <div className={styles.uploadOptionIcon}>
                      <svg width="32" height="32" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#f4f7fb" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Apple Photos</span>
                  </div>
                </div>
                {/* Row 3 (compact, cloud sources) */}
                <div className={`${styles.uploadOptionsRow} ${styles.uploadOptionsRowCompact}`}>
                  <div aria-hidden="true" className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay5} ${styles.hideOnMobile}`} onClick={() => handleCloudConnect('dropbox')}>
                    <div className={styles.uploadOptionIcon}>
                      <svg width="40" height="40" viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4-2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Dropbox</span>
                  </div>
                  <div aria-hidden="true" className={`${styles.uploadOptionBox} ${styles.optionSlideIn} ${styles.optionDelay6} ${styles.hideOnMobile}`} onClick={() => handleCloudConnect('googledrive')}>
                    <div className={styles.uploadOptionIcon}>
                      <svg width="32" height="32" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                        <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                        <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                        <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                        <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                        <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                      </svg>
                    </div>
                    <span className={styles.uploadOptionText}>Google Drive</span>
                  </div>
                </div>
              </div>
            </div>
            )}

            {showQrCode && (
            <div className={styles.flipCardBack}>
              <div className={styles.qrCodeFlipContent}>
                {/* The QR canvas is black-on-white, so it keeps its own white
                    plate on this dark panel to stay scannable. */}
                <div className={styles.qrCodeCanvasBox}>
                  <QRCodeComponent
                    url={contributeQrUrl}
                    size={200}
                  />
                </div>
                <p className={styles.qrCodeHint}>Scan with your phone camera to upload files from your mobile device</p>
                <div className={styles.qrCopyRow}>
                  <span className={styles.qrCopyUrl} title={contributeQrUrl}>{contributeQrUrl}</span>
                  <button type="button" className={styles.qrCopyButton} onClick={handleCopyQrLink}>
                    {linkCopied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <button className={styles.flipBackButton} onClick={() => { setShowQrCode(false); setShowAddMore(false); }} type="button">
                  ← Back to uploads
                </button>
              </div>
            </div>
            )}
          </div>

          {showAddMore && selectedFiles.length > 0 && (
            <div className={styles.backToFilesContainer}>
              <button type="button" className={styles.backToFilesButton} onClick={() => { setShowAddMore(false); setShowQrCode(false); }}>
                &larr; Back to selected files
              </button>
            </div>
          )}
        </div>
      )}
      {/* ── Selected Files Container ────────────────────────────────────────── */}
      {showFilesContainer && (
        <div className={styles.contentContainer}>
          <h2 className={styles.stepSubHeading}>
            {ownerFirstName ? `Add Media To ${ownerFirstName}'s Projector` : 'Add Media To Their Projector'}
          </h2>
          {ownerEmail && (
            <p className={styles.stepSubText}>{`You are adding media to ${ownerEmail}'s account`}</p>
          )}
          {hasExisting && (
            <p className={styles.stepSubText}>
              Files you added with
              {' '}
              <strong style={{ color: 'var(--lp-text)' }}>{contributorEmail.trim()}</strong>
              . Published items can’t be removed.
            </p>
          )}

          <div className={styles.uploadProgressContainer}>
            <div className={styles.quickAddRow}>
              {openFolder ? (
                // ── Level 2: inside a contributor's folder ──
                (
<div className={styles.folderNav}>
                  <div className={styles.folderNavTop}>
                    <button
                      type="button"
                      className={styles.tabBackButton}
                      onClick={() => setOpenFolderKey(null)}
                      aria-label="Back to folders"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 12H5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 19L5 12L12 5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <span className={styles.folderTabLabel} title={openFolder.email || openFolder.displayName}>
                      {openFolder.displayName}
                    </span>
                  </div>
                  <div className={styles.contentFilterTabs}>
                    {[
                      { label: 'All', value: 'all' },
                      { label: 'Videos', value: 'videos' },
                      { label: 'Photos', value: 'photos' },
                    ].map((tab) => (
                      <button
                        key={tab.value}
                        type="button"
                        className={`${styles.contentFilterTab} ${contentFilter === tab.value ? styles.contentFilterTabActive : ''}`}
                        onClick={() => setContentFilter(tab.value)}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
</div>
)
              ) : (
                // ── Level 1: Folders / Videos / Photos ──
                (
<div className={styles.contentFilterTabs}>
                  {[
                    { label: 'Folders', value: 'folders' },
                    { label: 'Videos', value: 'videos' },
                    { label: 'Photos', value: 'photos' },
                  ].map((tab) => (
                    <button
                      key={tab.value}
                      type="button"
                      className={`${styles.contentFilterTab} ${topTab === tab.value ? styles.contentFilterTabActive : ''}`}
                      onClick={() => setTopTab(tab.value)}
                    >
                      {tab.label}
                    </button>
                  ))}
</div>
)
              )}

              <div className={styles.quickAddRight}>
                <p style={{
                  color: 'var(--lp-text)', fontSize: '1rem', fontWeight: '600', fontFamily: "'Montserrat', sans-serif", margin: '0 0 8px 0',
                }}
                >
                  Add More Media
                </p>
                <div className={styles.quickAddIcons}>
                  {/* 1. Choose Files */}
                  <div className={styles.quickAddOption} onClick={() => filesInputRef.current?.click()}>
                    <svg className={styles.quickAddSvg} viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M14 2H6C4.9 2 4 2.9 4 4V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V8L14 2Z" />
                      <path d="M14 2V8H20" />
                      <path d="M12 18V12" />
                      <path d="M9 15L12 12L15 15" />
                    </svg>
                  </div>
                  {/* 2. Upload From Mobile */}
                  <div className={`${styles.quickAddOption} ${styles.quickAddHideMobile}`} onClick={() => { setShowAddMore(true); setShowQrCode(true); }}>
                    <svg className={styles.quickAddSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <rect x="5" y="1" width="14" height="22" rx="3" stroke="white" strokeWidth="1.5" />
                      <line x1="10" y1="3.5" x2="14" y2="3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                      <circle cx="12" cy="19" r="1" fill="white" />
                    </svg>
                  </div>
                  {/* 3. Apple Photos */}
                  <div className={`${styles.quickAddOption} ${styles.quickAddHideMobile}`} onClick={() => handleCloudConnect('applephotos')}>
                    <svg className={styles.quickAddSvg} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.05 20.28c-.98.95-2.05.79-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" fill="#999" />
                    </svg>
                  </div>
                  {/* 4. Google Photos */}
                  <div className={styles.quickAddOption} onClick={() => handleCloudConnect('googlephotos')}>
                    <svg className={styles.quickAddSvg} viewBox="0 0 122.88 122.88" xmlns="http://www.w3.org/2000/svg">
                      <path d="M30.72,27.97c16.95,0,30.72,13.77,30.72,30.72v2.83H2.83c-1.52,0-2.75-1.23-2.83-2.83 C0,41.73,13.77,27.97,30.72,27.97L30.72,27.97z" fill="#F6B704" />
                      <path d="M94.91,30.72c0,16.95-13.77,30.72-30.72,30.72l-2.75,0V2.83C61.44,1.3,62.67,0,64.19,0 C81.22,0,94.91,13.77,94.91,30.72L94.91,30.72z" fill="#E54335" />
                      <path d="M92.16,94.99c-16.95,0-30.72-13.77-30.72-30.72v-2.83h58.61c1.52,0,2.83,1.23,2.83,2.75 C122.88,81.22,109.11,94.99,92.16,94.99L92.16,94.99L92.16,94.99z" fill="#4280EF" />
                      <path d="M27.97,92.16c0-16.95,13.77-30.72,30.72-30.72h2.75v58.61c0,1.52-1.23,2.75-2.83,2.83 C41.66,122.88,27.97,109.11,27.97,92.16L27.97,92.16L27.97,92.16z" fill="#34A353" />
                    </svg>
                  </div>
                  {/* 5. Dropbox */}
                  <div className={styles.quickAddOption} onClick={() => handleCloudConnect('dropbox')}>
                    <svg className={styles.quickAddSvg} viewBox="0 0 43 40" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.5 0L0 8.1l8.6 6.9 12.5-7.8L12.5 0zM0 21.9l12.5 8.1 8.6-7.1-12.5-7.8L0 21.9zM21.1 22.9l8.6 7.1 12.5-8.1-8.6-6.8-12.5 7.8zM42.2 8.1L29.7 0l-8.6 7.2 12.5 7.8 8.6-6.9zM21.2 24.6l-8.6 7.1-4-2.6v2.9l12.6 7.6 12.6-7.6v-2.9l-4 2.6-8.6-7.1z" fill="#0061ff" />
                    </svg>
                  </div>
                  {/* 6. Google Drive */}
                  <div className={styles.quickAddOption} onClick={() => handleCloudConnect('googledrive')}>
                    <svg className={styles.quickAddSvg} viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5l5.4 9.35z" fill="#0066DA" />
                      <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3L1.2 52.35c-.8 1.4-1.2 2.95-1.2 4.5h27.5L43.65 25z" fill="#00AC47" />
                      <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57c.8-1.4 1.2-2.95 1.2-4.5H59.85L73.55 76.8z" fill="#EA4335" />
                      <path d="M43.65 25L57.4 1.2c-1.35-.8-2.9-1.2-4.5-1.2H34.35c-1.6 0-3.15.45-4.5 1.2L43.65 25z" fill="#00832D" />
                      <path d="M59.85 53H27.5l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.55c1.6 0 3.15-.45 4.5-1.2L59.85 53z" fill="#2684FC" />
                      <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.2 28h27.5c0-1.55-.4-3.1-1.2-4.5l-12.75-22z" fill="#FFBA00" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Limit error also needs to render in the files-container state:
                the "Choose Files" button above stays in this view, so a
                rejected selection (over the owner's remaining slots) would
                otherwise drop files silently with no message. */}
            {limitError && (
              <p style={{
                color: 'var(--lp-error)', fontSize: '1rem', fontWeight: 600, margin: '4px 0 12px', textAlign: 'center',
              }}
              >
                {limitError}
              </p>
            )}

            {/* Grid content depends on the level/tab: Level-1 Folders shows one
                folder card per contributor; Level-1 Videos/Photos flatten
                everyone's items; inside a folder we show that contributor's
                items filtered by the All/Videos/Photos sub-tab. */}
            <div className={`${styles.uploadProgressList} ${styles.filteredGrid}`}>
              {(() => {
                let cards;
                let cardRenderer = renderFilteredCard;
                if (openFolder) {
                  cards = folderFilteredCards;
                } else if (topTab === 'videos') {
                  cards = allVideos;
                } else if (topTab === 'photos') {
                  cards = allAlbums;
                } else {
                  cards = folders;
                  cardRenderer = renderFolderCard;
                }
                if (!cards.length) {
                  return <p className={styles.emptyTabNote}>Nothing here yet.</p>;
                }
                return cards.map(cardRenderer);
              })()}
            </div>

          </div>
        </div>
      )}
      {/* ── Album Photos Modal (same as QuickUserUploadPage) ────────────────── */}
      {albumModal && (
        <div
          className={styles.albumGridOverlay}
          role="button"
          tabIndex={0}
          onClick={() => setAlbumModal(null)}
          onKeyDown={(e) => { if (e.key === 'Escape') setAlbumModal(null); }}
        >
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <div
            role="dialog"
            aria-modal="true"
            className={styles.albumGridModal}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button type="button" className={styles.albumGridModalClose} onClick={() => setAlbumModal(null)}>
              &times;
            </button>
            <div className={styles.albumGridModalHeader}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#5aa5ef"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
              {`Photo Album (${albumModal.photos?.length || 0} photos)`}
            </div>
            <div className={styles.albumGridModalBody}>
              {albumModal.photos && albumModal.photos.length > 0 ? (
                <div className={styles.albumGridPhotos}>
                  {albumModal.photos.map((photo) => (
                    <div key={photo.id} className={styles.albumGridPhotoItem}>
                      {(photo.previewUrl || photo.thumbnailUrl) ? (
                        <img src={photo.previewUrl || photo.thumbnailUrl} alt={photo.name} />
                      ) : (
                        <div className={styles.albumGridPhotoPlaceholder}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="#555"><path d="M22 16V4c0-1.1-.9-2-2-2H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2zm-11-4l2.03 2.71L16 11l4 5H8l3-4zM2 6v14c0 1.1.9 2 2 2h14v-2H4V6H2z" /></svg>
                        </div>
                      )}
                      <span className={styles.albumGridPhotoName}>{photo.name}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--lp-muted)', textAlign: 'center', padding: '20px' }}>No photos</p>
              )}
            </div>
          </div>
        </div>
      )}
      {/* ── Cloud Upload Modal ──────────────────────────────────────────────── */}
      <CloudUploadModal
        open={cloudModalOpen}
        onClose={handleCloudModalClose}
        service={selectedCloudService}
        onFilesSelected={handleCloudFilesSelected}
        onShowMessage={(message) => setAppSnackbar({ isVisible: true, message, type: 'default' })}
      />
    </div>
  );
}

export { ContributePage };
export default ContributePage;
